import { samlFederationTranslations } from '@adornis/authentication/saml-client/translations.js';
import { baseConfig } from '@adornis/config/baseConfig.js';
import { dataViewTranslations } from '@adornis/data-view/default-translations.js';
import { fieldManagementTranslations } from '@adornis/field-management/default-translations.js';
import { filterTranslations } from '@adornis/filter/default-translations.js';
import { InstitutionLegalDoc } from '../entities/institutionLegalDoc.js';
import { ParticipationState } from '../enums/enums.js';
import { fieldInfoTranslations } from './field-info-translations.js';

// Demo
const demo = {
  field: {
    salutation: ['Anrede', 'Salutation'],
    title: ['Titel', 'Title'],
    phoneNumber: ['Telefonnummer', 'Phone number'],
    matrikelnummer: ['Matrikelnummer', 'Student ID'],
    department: ['Fachbereich', 'Department'],
    hessHochschulDepartment: ['Fachbereich', 'Department'],
    occupation: ['Beschäftigt als', 'Working as'],
    invoiceReceiver: ['Rechnung an', 'Invoice to'],
    institution: ['Institution', 'Institution'],
    institutionEmail: ['E-Mail', 'E-Mail'],
    division: ['Institution/Abteilung/Arbeitsbereich', 'Division'],
    postbox: ['Hauspostfach', 'Post box'],
    costCenter: ['Kostenstelle', 'Cost center'],
    attention: ['Zu Händen', 'Attention'],
    streetAndHousenumber: ['Straße und Hausnummer', 'Street'],
    zip: ['PLZ', 'ZIP'],
    city: ['Stadt', 'City'],
    externalDepartmentUniversity: ['Extern: Universität', 'External: University'],
    externalDepartmentDepartment: ['Extern: Fachbereich', 'External: Department'],
    otherDepartmentFurtherInformation: ['Sonstige: Weitere Informationen', 'Others: Further information'],
    doNotSendInvoice: ['erhält keine Rechnung', 'should not receive invoice'],
  },
  enums: {
    DEMOFRAU: ['Frau', 'Ms/Mrs'],
    DEMOHERR: ['Herr', 'Mr'],
    DEMOSONSTIGES: ['Keine Anrede', 'Other'],

    DEMOPROF_GOETHE: ['Prof.*in der Goethe-Universität', 'Prof.*in der Goethe-Universität'],
    DEMOSINCE_GOETHE: ['Wiss. Mitarb. der Goethe-Universität', 'Wiss. Mitarb. der Goethe-Universität'],
    DEMOTEACHER_GOETHE: ['Lehrbeauftragte*r der Goethe-Universität', 'Lehrbeauftragte*r der Goethe-Universität'],
    DEMOHIWI_GOETHE: ['Stud. Mitarb. der Goethe-Universität', 'Stud. Mitarb. der Goethe-Universität'],
    DEMOEMPLOYEE_GOETHE: [
      'Techn.-Admin. Mitarb. der Goethe-Universität',
      'Techn.-Admin. Mitarb. der Goethe-Universität',
    ],
    DEMOTEACHER: ['Lehrer*in im Schuldienst', 'Lehrer*in im Schuldienst'],
    DEMOHESS_HOCHSCHULANGEHOERIGE: [
      'Angehörige*r einer anderen Hochschule (nicht GU)',
      'Angehörige*r einer anderen Hochschule (nicht GU)',
    ],
    DEMOSONSTIGE: ['Sonstige', 'Sonstige'],
    DEMOCHEMIE: ['14 - Biochemie, Chemie und Pharmazie', '14 - Biochemie, Chemie und Pharmazie'],
    DEMOBIOLOGIE: ['15 - Biowissenschaften', '15 - Biowissenschaften'],
    DEMOERZIEHUNG: ['04 - Erziehungswissenschaften', '04 - Erziehungswissenschaften'],
    DEMOEVANG_THEOLOGIE: ['06 - Evangelische Theorie', '06 - Evangelische Theorie'],
    DEMOGEO: ['11 - Geowissenschaften / Geographie', '11 - Geowissenschaften / Geographie'],
    DEMOGESELLSCHAFT: ['03 - Gesellschaftswissenschaften', '03 - Gesellschaftswissenschaften'],
    DEMOINFORMATIK: ['12 - Informatik und Mathematik', '12 - Informatik und Mathematik'],
    DEMOKATH_THEOLOGIE: ['07 - Katholische Theologie', '07 - Katholische Theologie'],
    DEMOMEDIZIN: ['16 - Medizin', '16 - Medizin'],
    DEMONEU_PHILOLOGIE: ['10 - Neuere Philologien', '10 - Neuere Philologien'],
    DEMOPHILOSOPHIE: ['08 - Philosophie und Geschichtswissenschaften', '08 - Philosophie und Geschichtswissenschaften'],
    DEMOPHYSIK: ['13 - Physik', '13 - Physik'],
    DEMOPSYCHOLOGIE: ['05 - Psychologie und Sportwissenschaften', '05 - Psychologie und Sportwissenschaften'],
    DEMORECHT: ['01 - Rechtswissenschaft', '01 - Rechtswissenschaft'],
    DEMOSPRACHEN: ['09 - Sprach- und Kulturwissenschaften', '09 - Sprach- und Kulturwissenschaften'],
    DEMOWIRTSCHAFT: ['02 - Wirtschaftswissenschaften', '02 - Wirtschaftswissenschaften'],
    DEMOEXTERN: ['Extern', 'Extern'],
    DEMOZENTRALE: ['Zentrale Einrichtungen GU', 'Zentrale Einrichtungen GU'],
    DEMOOTHERS: ['Sonstige', 'Sonstige'],
  },
};

// IKH
const ikh = {
  field: {
    salutation: ['Anrede', 'Salutation'],
    title: ['Titel', 'Title'],
    phoneNumber: ['Telefonnummer', 'Phone number'],
    department: ['Fachbereich', 'Department'],
    hessHochschulDepartment: ['Fachbereich', 'Department'],
    occupation: ['Beschäftigt als', 'Working as'],
    invoiceReceiver: ['Rechnung an', 'Invoice to'],
    institution: ['Institution', 'Institution'],
    institutionEmail: ['E-Mail', 'E-Mail'],
    division: ['Institut/Abteilung/Arbeitsbereich', 'Division'],
    postbox: ['Hauspostfach', 'Post box'],
    costCenter: ['Kostenstelle', 'Cost center'],
    attention: ['Zu Händen', 'Attention'],
    streetAndHousenumber: ['Straße und Hausnummer', 'Street'],
    zip: ['PLZ', 'ZIP'],
    city: ['Stadt', 'City'],
    externalDepartmentUniversity: ['Extern: Universität', 'External: University'],
    externalDepartmentDepartment: ['Extern: Fachbereich', 'External: Department'],
    otherDepartmentFurtherInformation: ['Sonstige: Weitere Informationen', 'Others: Further information'],
    doNotSendInvoice: ['erhält keine Rechnung', 'should not receive invoice'],
  },
  enums: {
    IKHFRAU: ['Frau', 'Ms/Mrs'],
    IKHHERR: ['Herr', 'Mr'],
    IKHSONSTIGES: ['Keine Anrede', 'Other'],

    IKHPROF_GOETHE: ['Prof.*in der Goethe-Universität', 'Prof.*in der Goethe-Universität'],
    IKHSINCE_GOETHE: ['Wiss. Mitarb. der Goethe-Universität', 'Wiss. Mitarb. der Goethe-Universität'],
    IKHTEACHER_GOETHE: ['Lehrbeauftragte*r der Goethe-Universität', 'Lehrbeauftragte*r der Goethe-Universität'],
    IKHHIWI_GOETHE: ['Stud. Mitarb. der Goethe-Universität', 'Stud. Mitarb. der Goethe-Universität'],
    IKHEMPLOYEE_GOETHE: [
      'Techn.-Admin. Mitarb. der Goethe-Universität',
      'Techn.-Admin. Mitarb. der Goethe-Universität',
    ],
    IKHTEACHER: ['Lehrer*in im Schuldienst', 'Lehrer*in im Schuldienst'],
    IKHHESS_HOCHSCHULANGEHOERIGE: [
      'Angehörige*r einer anderen Hochschule (nicht GU)',
      'Angehörige*r einer anderen Hochschule (nicht GU)',
    ],
    IKHSONSTIGE: ['Sonstige', 'Sonstige'],
    IKHCHEMIE: ['14 - Biochemie, Chemie und Pharmazie', '14 - Biochemie, Chemie und Pharmazie'],
    IKHBIOLOGIE: ['15 - Biowissenschaften', '15 - Biowissenschaften'],
    IKHERZIEHUNG: ['04 - Erziehungswissenschaften', '04 - Erziehungswissenschaften'],
    IKHEVANG_THEOLOGIE: ['06 - Evangelische Theorie', '06 - Evangelische Theorie'],
    IKHGEO: ['11 - Geowissenschaften / Geographie', '11 - Geowissenschaften / Geographie'],
    IKHGESELLSCHAFT: ['03 - Gesellschaftswissenschaften', '03 - Gesellschaftswissenschaften'],
    IKHINFORMATIK: ['12 - Informatik und Mathematik', '12 - Informatik und Mathematik'],
    IKHKATH_THEOLOGIE: ['07 - Katholische Theologie', '07 - Katholische Theologie'],
    IKHMEDIZIN: ['16 - Medizin', '16 - Medizin'],
    IKHNEU_PHILOLOGIE: ['10 - Neuere Philologien', '10 - Neuere Philologien'],
    IKHPHILOSOPHIE: ['08 - Philosophie und Geschichtswissenschaften', '08 - Philosophie und Geschichtswissenschaften'],
    IKHPHYSIK: ['13 - Physik', '13 - Physik'],
    IKHPSYCHOLOGIE: ['05 - Psychologie und Sportwissenschaften', '05 - Psychologie und Sportwissenschaften'],
    IKHRECHT: ['01 - Rechtswissenschaft', '01 - Rechtswissenschaft'],
    IKHSPRACHEN: ['09 - Sprach- und Kulturwissenschaften', '09 - Sprach- und Kulturwissenschaften'],
    IKHWIRTSCHAFT: ['02 - Wirtschaftswissenschaften', '02 - Wirtschaftswissenschaften'],
    IKHEXTERN: ['Extern', 'Extern'],
    IKHZENTRALE: ['Zentrale Einrichtungen GU', 'Zentrale Einrichtungen GU'],
    IKHOTHERS: ['Sonstige', 'Sonstige'],
    IKHINTERNAL: [
      'Intern (via Kostenstelle der Goethe Universität)',
      'Internal (via cost center of the Goethe university)',
    ],
    IKHEXTERNAL: ['Externe Institution (nicht Goethe Universität)', 'External Institution (not Goethe university)'],
    IKHPRIVATE: ['Privat', 'Private'],
  },
};

// StudiumDigitale
const sd = {
  field: {
    salutation: ['Anrede', 'Salutation'],
    title: ['Titel', 'Title'],
    phoneNumber: ['Telefonnummer', 'Phone number'],
    matrikelnummer: ['Matrikelnummer', 'Student ID'],
    department: ['Fachbereich', 'Department'],
    occupation: ['Beschäftigt als', 'Working as'],
    invoiceReceiver: ['Rechnung an', 'Invoice to'],
    institution: ['Institution', 'Institution'],
    division: ['Institution/Abteilung/Arbeitsbereich', 'Division'],
    postbox: ['Hauspostfach', 'Post box'],
    attention: ['Zu Händen', 'Attention'],
    address: ['Adresse', 'Address'],
    streetAndHousenumber: ['Straße und Hausnummer', 'Street'],
    zip: ['PLZ', 'ZIP'],
    city: ['Stadt', 'City'],
    externalDepartmentUniversity: ['Extern: Universität', 'External: University'],
    externalDepartmentDepartment: ['Extern: Fachbereich', 'External: Department'],
    otherDepartmentFurtherInformation: ['Sonstige: Weitere Informationen', 'Others: Further information'],
    tutoriumsTitle: ['Tutoriums Titel', 'Tutorium title'],
    unterstuetzungsBedarf: ['Unterstützungsbedarf', 'Need for support'],
    birthday: ['Geburtstag', 'Birthday'],
  },
  enums: {
    SDFRAU: ['Frau', 'Ms/Mrs'],
    SDHERR: ['Herr', 'Mr'],
    SDSONSTIGES: ['Sonstiges', 'Other'],

    SDHIWI_GOETHE: [
      'Lehrende/r / Wiss. Mitarb. der Goethe-Universität',
      'Lehrende/r / Wiss. Mitarb. der Goethe-Universität',
    ],
    SDSTUDEMPLOYEE_GOETHE: ['Stud. Mitarb. der Goethe-Universität', 'Stud. Mitarb. der Goethe-Universität'],
    SDTEACHER: ['Lehrer/in', 'Lehrer/in'],
    SDHESS_HOCHSCHULANGEHOERIGE: ['Hess. Hochschulangehörige/r', 'Hess. Hochschulangehörige/r'],
    SDSONSTIGE: ['Sonstige', 'Sonstige'],
    SDUB_GOETHE: ['UB / Goethe Universität', 'UB / Goethe Universität'],
    SDEMPLOYEE_GOETHE: ['Angestellte/r Goethe Universität', 'Angestellte/r Goethe Universität'],
    SDCHEMIE: ['14 - Biochemie, Chemie und Pharmazie', '14 - Biochemie, Chemie und Pharmazie'],
    SDBIOLOGIE: ['15 - Biowissenschaften', '15 - Biowissenschaften'],
    SDERZIEHUNG: ['04 - Erziehungswissenschaften', '04 - Erziehungswissenschaften'],
    SDEVANG_THEOLOGIE: ['06 - Evangelische Theorie', '06 - Evangelische Theorie'],
    SDGEO: ['11 - Geowissenschaften / Geographie', '11 - Geowissenschaften / Geographie'],
    SDGESELLSCHAFT: ['03 - Gesellschaftswissenschaften', '03 - Gesellschaftswissenschaften'],
    SDINFORMATIK: ['12 - Informatik und Mathematik', '12 - Informatik und Mathematik'],
    SDKATH_THEOLOGIE: ['07 - Katholische Theologie', '07 - Katholische Theologie'],
    SDMEDIZIN: ['16 - Medizin', '16 - Medizin'],
    SDUNIKLINIKUM: ['Uniklinikum Frankfurt', 'Uniklinikum Frankfurt'],
    SDNEU_PHILOLOGIE: ['10 - Neuere Philologien', '10 - Neuere Philologien'],
    SDPHILOSOPHIE: ['08 - Philosophie und Geschichtswissenschaften', '08 - Philosophie und Geschichtswissenschaften'],
    SDPHYSIK: ['13 - Physik', '13 - Physik'],
    SDPSYCHOLOGIE: ['05 - Psychologie und Sportwissenschaften', '05 - Psychologie und Sportwissenschaften'],
    SDRECHT: ['01 - Rechtswissenschaft', '01 - Rechtswissenschaft'],
    SDSPRACHEN: ['09 - Sprach- und Kulturwissenschaften', '09 - Sprach- und Kulturwissenschaften'],
    SDWIRTSCHAFT: ['02 - Wirtschaftswissenschaften', '02 - Wirtschaftswissenschaften'],
    SDEXTERN: ['Extern', 'Extern'],
    SDZENTRALE: ['Zentrale Einrichtungen GU', 'Zentrale Einrichtungen GU'],
    SDOTHERS: ['Sonstige', 'Sonstige'],

    SDINTERNAL: [
      'Intern (via Kostenstelle der Goethe Universität)',
      'Internal (via cost center of the Goethe university)',
    ],
    SDEXTERNAL: ['Externe Institution (nicht Goethe Universität)', 'External Institution (not Goethe university)'],
    SDPRIVATE: ['Privat', 'Private'],
  },
};

const sdel = {
  field: {},
  enums: {
    SDEL_JA: ['Ja', 'Yes'],
    SDEL_NEIN: ['Nein', 'No'],
  },
};

const cpg = {
  field: {},
  enums: {
    CPG_FB1: ['FB 01 | Rechtswissenschaft'],
    CPG_FB2: ['FB 02 | Wirtschaftswissenschaften'],
    CPG_FB3: ['FB 03 | Gesellschaftswissenschaften'],
    CPG_FB4: ['FB 04 | Erziehungswissenschaften'],
    CPG_FB5: ['FB 05 | Psychologie und Sportwissenschaften'],
    CPG_FB6: ['FB 06 | Evangelische Theologie'],
    CPG_FB7: ['FB 07 | Katholische Theologie'],
    CPG_FB8: ['FB 08 | Philosophie und Geschichtswissenschaften'],
    CPG_FB9: ['FB 09 | Sprach- und Kulturwissenschaften'],
    CPG_FB10: ['FB 10 | Neuere Philologien'],
    CPG_FB11: ['FB 11 | Geowissenschaften / Geographie'],
    CPG_FB12: ['FB 12 | Informatik und Mathematik'],
    CPG_FB13: ['FB 13 | Physik'],
    CPG_FB14: ['FB 14 | Biochemie, Chemie und Pharmazie'],
    CPG_FB15: ['FB 15 | Biowissenschaften'],
    CPG_FB16: ['FB 16 | Medizin'],
    CPG_SONSTIGES: ['Sonstiges'],
  },
};

const pbs = {
  field: {},
  enums: {
    PBS_FB1: ['FB 01 Rechtswissenschaft', 'F 01 Faculty of Law'],
    PBS_FB2: ['FB 02 Wirtschaftswissenschaften', 'F 02 Faculty of Economics and Business '],
    PBS_FB3: ['FB 03 Gesellschaftswissenschaften', 'F 03 Faculty of Social Sciences'],
    PBS_FB4: ['FB 04 Erziehungswissenschaften', 'F 04 Faculty of Educational Sciences'],
    PBS_FB5: ['FB 05 Psychologie und Sportwissenschaften', 'F 05 Faculty of Psychology and Sports Sciences'],
    PBS_FB6: ['FB 06 Evangelische Theologie', 'F 06 Faculty of Protestant Theology'],
    PBS_FB7: ['FB 07 Katholische Theologie', 'F 07 Faculty of Catholic Theology'],
    PBS_FB8: ['FB 08 Philosophie und Geschichtswissenschaften', 'F 08 Faculty of Philosophy and History'],
    PBS_FB9: ['FB 09 Sprach- und Kulturwissenschaften', 'F 09 Faculty of Linguistics, Cultures and Arts'],
    PBS_FB10: ['FB 10 Neuere Philologien', 'F 10 Faculty of Modern Languages'],
    PBS_FB11: ['FB 11 Geowissenschaften und Geographie', 'F 11 Faculty of Geosciences and Geography'],
    PBS_FB12: ['FB 12 Informatik und Mathematik', 'F 12 Faculty of Computer Science and Mathematics'],
    PBS_FB13: ['FB 13 Physik', 'F 13 Faculty of Physics'],
    PBS_FB14: ['FB 14 Biochemie, Chemie und Pharmazie', 'F 14 Faculty of Biochemistry, Chemistry and Pharmacy'],
    PBS_FB15: ['FB 15 Biowissenschaften', 'F 15 Faculty of Biological Sciences'],
    PBS_FB16: ['FB 16 Medizin', 'F 16 Faculty of Medicine'],
  },
};

const st = {
  field: {},
  enums: {
    ST_Q1: ['Q1'],
    ST_Q2: ['Q2'],
    ST_Q3: ['Q3'],
    ST_Q4: ['Q4'],
    ST_SONSTIGES: ['Sonstiges'],
  },
};

const fjn = {
  field: { herkunft: ['Anreise', 'Arrival'] },
  enums: {
    FJN_FRANKFURT: ['Frankfurt', 'Frankfurt'],
    FJN_RHEINMAIN: ['Rhein-Main', 'Rhein-Main'],
    FJN_MORE_THAN_25: ['>25 km entfernt', '>25 km'],
    FJN_MORE_THAN_50: ['>50 km entfernt', '>50 km'],
  },
};

const ttt = {
  field: {
    consultationRequestDescription: [
      'Möchten Sie eine Beratung anfragen? Dann lassen Sie uns hier kurz wissen, worum es geht.',
      'Do you want to make a request for a consultation? Please let us know what it is about.',
    ],
  },
};

//DigiTeLL

const digitell = {
  field: {
    organisation: ['Organisation', 'organisation'],
    department: ['Fachbereich', 'department'],
    bitteTeilenSieUnsIhreBesonderenErnhrungsgewohnheitenUndAllergienMit: [
      'Bitte teilen Sie uns Ihre Besondere Ernährungsgewohnheiten und Allergien mit',
      'please share your special diet requirements',
    ],
    habenSieWeitereAnregungenWnscheBarrierefreiheitRuheraumEtc: [
      'Haben Sie weitere Anregungen, Wünsche, Barrierefreiheit, Rüheraum, etc?',
      'do you have any further suggestions for accessibility, space, etc.',
    ],
    ichBinDamitEinverstandenInDieTeilnehmerlisteAufgenommenZuWerden: [
      'Ich bin damit einverstanden, in die Teilnehmerliste aufgenommen zu werden',
      'I agree to be included in the participant list',
    ],
    ichBinEinverstandenDassAmVeranstaltungstagFotosUndVideosVonMirGemachtWerden: [
      'Ich bin einverstanden, dass am Veranstaltungstag Fotos und Videos von mir gemacht werden',
      'I agree that photos and videos will be taken at the event',
    ],
    ichMchteAmMittagessenTeilnehmen: [
      'Bitte teilen Sie uns mit, ob Sie beim Mittagessen teilnehmen?',
      'Do you participate at lunch?',
    ],
    ichMchteAmAbendessenTeilnehmen: [
      'Bitte teilen Sie uns mit, ob Sie beim  Abendessen zur Jubiläumsfeier von studiumdigitale teilnehmen?',
      'Please let us know if you will be attending the dinner for the anniversary celebration of studiumdigitale?',
    ],
  },
  enums: {
    DIGITELL_JA: ['Ja', 'Yes'],
    DIGITELL_NEIN: ['Nein', 'No'],
  },
};

//Methodenzentrum Sozialwissenschaften (MZS)
const mzs = {
  field: {
    salutation: ['Anrede', 'Salutation'],
    currentUni: ['Hochschule', 'university'],
    institution: ['Institution', 'department'],
    institutionOther: ['Sonstige Institution', 'other institution'],
    desiredDegree: ['angestrebter Abschluss', 'desired degreee'],
    desiredDegreeOther: ['Sonstiger Abschluss', 'other degree'],
    statusGroup: ['Status Gruppe', 'status group'],
    statusGroupOther: ['Sonstige Gruppe', 'other group'],
    hearAboutOffer: ['Wie haben Sie von unserem Angebot erfahren?', 'How did you hear about our offers?'],
    hearAboutOfferOther: ['Sonstige Möglichkeiten', 'other'],
    participatedBefore: [
      'Haben Sie schonmal an einem Workshop des Methodenzentrums teilgenommen?',
      'Did you participate in one of our workshops before?',
    ],
    participatedBeforeYes: ['Ja', 'Yes'],
    participatedBeforeNo: ['Nein', 'No'],
  },
  enums: {
    MZSFRAU: ['Frau', 'Ms.'],
    MZSHERR: ['Herr', 'Mr.'],
    MZSSONSTIGES: ['Sonstiges', 'Other'],

    MZS_BIOCHEMIE_CHEMIE_PHARMA: ['Biochemie, Chemie und Pharmazie', 'biochemisty, chemisty, pharmacy'],
    MZS_BIO: ['Biowissenschaften', 'biology'],
    MZS_ERZIEHUNG: ['Erziehungswissenschaften', 'pedagogy'],
    MZSEVANG_THEOLOGIE: ['Evangelische Theologie', 'protestant theology'],
    MZSGEO: ['Geowissenschaften / Geographie', 'geology / geography'],
    MZSGESELLSCHAFT: ['Gesellschaftswissenschaften', 'social sciences'],
    MZSINFORMATIK: ['Informatik und Mathematik', 'computer science and math'],
    MZSKATH_THEOLOGIE: ['Katholische Theologie', 'catholic theology'],
    MZSMEDIZIN: ['Medizin', 'medicine'],
    MZSNEU_PHILOLOGIE: ['Neuere Philologien', 'modern philologies'],
    MZSPHILOSOPHIE: ['Philosophie und Geschichtswissenschaften', 'philosophy and history'],
    MZSPHYSIK: ['Physik', 'physic'],
    MZSPSYCHOLOGIE: ['Psychologie und Sportwissenschaften', 'psychology and sports science'],
    MZSRECHT: ['Rechtswissenschaft', 'jurisprudence'],
    MZSSPRACHEN: ['Sprach- und Kulturwissenschaft', 'linguistics and cultural studies'],
    MZSWIRTSCHAFT: ['Wirtschaftswissenschaften', 'economics'],
    MZSOTHERS: ['Sonstiges', 'others'],

    MZS_BACHELOR: ['Bachelor', 'Bachelor'],
    MZS_MASTER: ['Master', 'Master'],
    MZS_STAATSEXAMEN_LEHRAMT: ['Staatsexamen Lehramt', 'Staatsexamen Lehramt'],
    MZS_STAATSEXAMEN_MEDIZIN_PHARMAZIE: ['Staatsexamen Medizin / Pharmazie', 'Staatsexamen Medizin / Pharmazie'],
    MZS_STAATSEXAMEN_JURA: ['Staatsexamen Jura', 'Staatsexamen Jura'],
    MZS_PROMOTION: ['Promotion', 'Promotion'],
    MZS_SONSTIGE: ['Sonstige', 'Other'],

    MZS_STUDENT: ['Studierende', 'students'],
    MZS_TEACHER: [
      'Lehrende/r/ Wissenschaftliche/r Mitarbeiter/in der Goethe Uni (und Sonstige)',
      'teacher/ employee at Goethe-University (and other)',
    ],
    // MZSEMPLOYEE_GOETHE: ['Mitarb. der Goethe-Universität', 'employee at Goethe-University'],
    // MZSSONSTIGE: ['Sonstiges', 'other'],

    MZS_FLYER: ['Flyer/Plakate', 'flyer/posters'],
    MZS_HOMEPAGE: ['Homepage', 'homepage'],
    MZS_SOCIAL_MEDIA: ['Social Media', 'social media'],
    MZS_INFO_STAND: ['Info-Stand', 'information booth'],
    MZS_COURSE: ['Lehrveranstaltung/Lehrende', 'course/teacher'],
    MZS_OTHER_STUDENTS: ['andere Studierende', 'other students'],
    MZS_OTHER_OPTION: ['Sonstiges', 'other'],
  },
};

const tuda = {
  isImmigrantExpl: [
    'Ich habe eine Zuwanderungsgeschichte (Mindestens ein Elternteil und/oder ich selbst wurde außerhalb Deutschlands geboren)',
    'at least one of my parents was born outside of Germany',
  ],
  field: {
    gender: ['Geschlecht', 'Gender'],
    dateOfBirth: ['Geburtsdatum', 'Date of Birth'],
    nationality: ['Nationalität', 'Nationality'],
    countryOfBirth: ['Geburtsland', 'Country of Birth'],
    isImmigrant: ['Zuwanderungsgeschichte', 'Immigration background'],
    phoneNumber: ['Telefonnummer', 'Phone number'],
    currentUni: ['derzeitige Hochschule', 'current college'],
    semester: ['Fachsemester', 'Semester'],
    courseOfStudy: ['Studiengang', 'Course of Study'],
    department: ['Fachbereich', 'Department'],
    desiredDegree: ['Angestrebter Abschluss', ' desired Degree'],
    jobHuntingDifficulties: ['Welche Schwierigkeiten hast du bei der Jobsuche?', 'Jobhunting Difficulties?'],
    participationReason: [
      'Warum möchtest du am Career Mentoring-Programm teilnehmen?',
      'Why do you want to partake in the mentoring Program',
    ],
    questions: [
      'Welche Fragen möchtest du mit deinem Mentor/ deiner Mentorin besprechen?',
      'Which questions do you want to discuss with your mentor',
    ],
    mentorExchangeFrequency: [
      'Wie oft möchtest du dich mit deinem Mentor/ deiner Mentorin austauschen?',
      'How frequent to you want to meet with you mentor',
    ],
    mentorExchangeMethod: ['Wie soll der Austausch stattfinden?', 'Where should your meeting take place'],
    mentorExchangeLanguage: [
      'In welcher Sprache möchtest du mit deinem Mentor/ deiner Mentorin sprechen?',
      'In which language do you want to communicate?',
    ],
    mentorMatchGender: ['Mit wem möchtest du gematcht werden?', 'With whom do you want to get machted?'],
    mentoringTopic: [
      'Welche Themen sind dir im Mentoring besonders wichtig? Bitte kreuze an. (max. 3 auswählen)',
      'Which topics are especially important to you?',
    ],
    individualizedTopic: ['andere Themen', 'other topics'],
    additionalInformation: ['Folgendes möchte ich noch mitteilen:', 'Additional import information'],
    streetAndHousenumber: ['Straße und Hausnummer', 'Street'],
    zip: ['PLZ', 'ZIP'],
    city: ['Stadt', 'City'],
  },
  enums: {
    TUDA_MALE: ['männlich', 'male'],
    TUDA_FEMALE: ['weiblich', 'female'],
    TUDA_DIVERSE: ['divers', 'diverse'],
    TUDA_NOT_SPECIFIED: ['ohne Angabe', 'not specfied'],

    TUDA_FRA: ['Frankfurt University of Applied Sciences', 'Frankfurt University of Applied Sciences'],
    TUDA_GU: ['Goethe-Universität Frankfurt', 'Goethe-Universität Frankfurt'],
    TUDA_HDA: ['Hochschule Darmstadt', 'Hochschule Darmstadt'],
    TUDA_HSRM: ['Hochschule RheinMain', 'Hochschule RheinMain'],
    TUDA_TUDA: ['Technische Universität Darmstadt', 'Technische Universität Darmstadt'],
    TUDA_JGU: ['Johannes Gutenberg-Universität Mainz', 'Johannes Gutenberg-Universität Mainz'],
    TUDA_ANDERE: ['Andere', 'Other'],

    TUDA_ARGICULTURE: ['Umwelt-, Agrar- & Forstwissenschaften', 'Agricultural, environmental or forest science'],
    TUDA_ARCHITECTURE: ['Architektur', 'Architecture'],
    TUDA_ARTS: ['Kunst, Musik, Design & Mode', 'Arts, Music, Design or Fashion'],
    TUDA_CIVIL_ENGINEERING: ['Bau- & Umweltingenierwissenschaften', 'Civil & Environmental Engineering'],
    TUDA_COMPUTER_SCIENCE: ['Informatik', 'Computer Science'],
    TUDA_ECONOMICS_MANAGEMENT: ['Wirtschaft & Management', 'Economics & Management'],
    TUDA_ELECTRICAL_ENGINEERING: ['Elektrotechnik', 'Electrical Engineering'],
    TUDA_ENGINEERING_TECHNOLOGY: ['Ingenieurwesen & Technologie', 'Engineering & Technology'],
    TUDA_EVENT_MANAGEMENT: ['Event-, Tourismus- & Gastgewerbe-Management', 'Event, Tourism or Hospitality Management'],
    TUDA_HEALTH: ['Gesundheit & Medizin', 'Health & Medicine'],
    TUDA_LAW: ['Recht, Steuern & Verwaltung', 'Law, Taxation, Public Administration'],
    TUDA_LINGUISTICS: ['Linguistik & Kulturwissenschaften', 'Linguistics & Cultural Science'],
    TUDA_MECHANICAL_ENGINEERING: ['Maschinenbau', 'Mechanical Engineering'],
    TUDA_MARKETING: ['Medien, Kommunikation & Marketing', 'Media, Communication & Marketing'],
    TUDA_MATHEMATICS: ['Naturwissenschaften & Mathematik', 'Natural Sciences & Mathematics'],
    TUDA_PSYCHOLOGY: ['Psychologie', 'Psychology'],
    TUDA_SOCIAL_SCIENCE: ['Sozialwissenschaften', 'Social Sciences'],
    TUDA_SPORTS: ['Sport & Fitness', 'Sports & Fitness'],
    TUDA_PEDAGOGY: ['Lehramt & Pädagogik', 'Teaching & Pedagogy'],
    TUDA_OTHER: ['Sonstiger Fachbereich', 'Other'],

    TUDA_BACHELOR: ['Bachelor', 'Bachelor'],
    TUDA_MASTER: ['Master', 'Master'],
    TUDA_STAATSEXAMEN_LEHRAMT: ['Staatsexamen Lehramt', 'Staatsexamen Lehramt'],
    TUDA_STAATSEXAMEN_MEDIZIN_PHARMAZIE: ['Staatsexamen Medizin / Pharmazie', 'Staatsexamen Medizin / Pharmazie'],
    TUDA_STAATSEXAMEN_JURA: ['Staatsexamen Jura', 'Staatsexamen Jura'],
    TUDA_PROMOTION: ['Promotion', 'Promotion'],
    TUDA_SONSTIGE: ['Sonstige', 'Other'],

    TUDA_MINDESTENS_1_MAL_PRO_WOCHE: ['mindestens einmal pro Woche', 'minimum once a week'],
    TUDA_ZWEI_MAL_PRO_MONAT: ['zwei mal im Monat', 'twice a month'],
    TUDA_SELTENER_ALS_ZWEIMAL_PRO_MONAT: ['seltener als zwei mal im Monat', 'less then twice a month'],

    TUDA_ONLINE_DIGITAL: ['online', 'online'],
    TUDA_IN_PRAESENZ: ['in Präsenz', 'in person'],
    TUDA_BEIDES: ['beides', 'both'],

    TUDA_DEUTSCH: ['Deutsch', 'German'],
    TUDA_ENGLISCH: ['Englisch', 'English'],
    TUDA_BOTH: ['beides', 'both'],

    TUDA_MATCH_MALE: ['Mann', 'Men'],
    TUDA_MATCH_FEMALE: ['Frau', 'Woman'],
    TUDA_MATCH_BOTH: ['beides', 'both'],

    TUDA_KONTAKTE_KNUPFEN: ['Kontakte knüpfen', 'Get to know other People'],
    TUDA_BRANCHEN_KENNENLERNEN: ['Branchen und Tätigkeitsfelder kennenlernen', 'Get to know other industries'],
    TUDA_ARBEITSMOEGLICHKEITEN_ERKUNDEN: [
      'Arbeitsmöglichkeiten im Rhein-Main-Gebiet erkunden',
      'discover employment opertunuties in Rhein-Mein area',
    ],
    TUDA_UNTERSTUEZUNG_DER_KARIEREPLANUNG: [
      'Unterstützung bei der Karriereplanung und -gestaltung',
      'Support in career planing',
    ],
    TUDA_EINBLICK_ARBEITSALLTAG: ['Einblick in Arbeitsalltag erhalten', 'Get to know every day working life'],
    TUDA_FACHLICHE_FRAGEN: ['Fachliche Fragen klären', 'technical questions'],
    TUDA_WORK_LIFE_BALANCE: [
      'Vereinbarkeit von Familie und Beruf „Work-Life-Balance“',
      'Questions about „Work-Life-Balance“',
    ],
    TUDA_FRAUEN_KARRIEREPLANUNG: ['Frauen Karriereplanung', 'carrer options for woman'],
    TUDA_KOMMUNIKATIONS_TRAINING: [
      'Training im Bereich Kommunikationsfähigkeiten und Selbstvermarktung',
      'communication training',
    ],
    TUDA_BEWERBUNGSPROZESS_UNTERTUETZUNG: [
      'Unterstützung im Bewerbungsprozess (Bewerbungsunterlagen/ Interview)',
      'application process support',
    ],
    TUDA_INTROSPEKTIVE: [
      'Persönliche Stärken und Schwächen erkennen',
      'Get to know your own stregths and weakness better',
    ],
    TUDA_ALLEINSTELLUNGSMERKMAL_FINDEN: ['Alleinstellungsmerkmal/ USP herausarbeiten', 'find you unique selling point'],
    TUDA_WEITERBILDUNGSMOEGLICHKEITEN: [
      'Tipps zu Weiterbildungsmöglichkeiten',
      'tips to find opertunities for more specialiced education',
    ],
    TUDA_ARBEITSTREFFEN_BEGLEITEN: [
      'Mentor/ Mentorin zu Arbeitstreffen begleiten (Meetings, Messen, etc.)',
      'Fllow your mentor in one of his meetings',
    ],
    TUDA_DEUTSCH_LERNEN: ['Deutsch lernen/ verbessern', 'improve your proficiency in german'],
    // TUDA_ANDERE: ['Andere', 'other'],
  },
};

const icsrm = {
  isImmigrantExpl: [
    'Ich habe eine Zuwanderungsgeschichte (Mindestens ein Elternteil und/oder ich selbst wurde außerhalb Deutschlands geboren)',
    'at least one of my parents was born outside of Germany',
  ],
  impactMeasurementTitle: ['Wirkungsmessung', 'Impact measurement'],
  // Endet bewusst offen - die verlinkte Datenschutzerklärung wird im Formular angehängt.
  impactMeasurementExpl: [
    'Ich bin damit einverstanden, dass mich der ICS RM zukünftig per E-Mail kontaktiert, um meine Erfahrungen mit den Angeboten zu erfragen (Wirkungsmessung). Die Teilnahme ist freiwillig; ich kann diese Einwilligung jederzeit ohne Angabe von Gründen mit Wirkung für die Zukunft widerrufen. Weitere Informationen zur Datenverarbeitung siehe ',
    'I agree that the ICS RM may contact me by e-mail in the future to ask about my experiences with its offers (impact measurement). Participation is voluntary; I can withdraw this consent at any time, without giving reasons, with effect for the future. For more information on data processing see ',
  ],
  privacyPolicyLabel: ['Datenschutzerklärung', 'privacy policy'],
  privateEmailExpl: [
    'Ich bin einverstanden, auch nach meinem Studienabschluss weiter kontaktiert zu werden und gebe hierfür freiwillig eine private E-Mail-Adresse an:',
    'I agree to be contacted after graduating as well, and voluntarily provide a private e-mail address for this purpose:',
  ],
  field: {
    gender: ['Geschlecht', 'Gender'],
    dateOfBirth: ['Geburtsdatum', 'Date of Birth'],
    nationality: ['Nationalität', 'Nationality'],
    countryOfBirth: ['Geburtsland', 'Country of Birth'],
    isImmigrant: ['Zuwanderungsgeschichte', 'Immigration background'],
    foreignUniQualification: [
      'Ich habe meine Hochschulzugangsberechtigung (höchster Schulabschluss) außerhalb Deutschlands erworben',
      'I gained my higher education entrance qualification (high school diploma or equivalent) outside of Germany',
    ],
    phoneNumber: ['Telefonnummer', 'Phone number'],
    currentUni: ['derzeitige Hochschule', 'current college'],
    semester: ['Fachsemester', 'Semester'],
    courseOfStudy: ['Studiengang', 'Course of Study'],
    department: ['Fachbereich', 'Department'],
    desiredDegree: ['Angestrebter Abschluss', ' desired Degree'],
    streetAndHousenumber: ['Straße und Hausnummer', 'Street'],
    zip: ['PLZ', 'ZIP'],
    city: ['Stadt', 'City'],
    impactMeasurementConsent: ['Einverständnis Wirkungsmessung', 'Consent to impact measurement'],
    privateEmail: ['Private E-Mail-Adresse', 'Private e-mail address'],
  },
  enums: {
    ICSRM_MALE: ['männlich', 'male'],
    ICSRM_FEMALE: ['weiblich', 'female'],
    ICSRM_DIVERSE: ['divers', 'diverse'],
    ICSRM_NOT_SPECIFIED: ['ohne Angabe', 'not specfied'],

    ICSRM_FRA: ['Frankfurt University of Applied Sciences', 'Frankfurt University of Applied Sciences'],
    ICSRM_GU: ['Goethe-Universität Frankfurt', 'Goethe-Universität Frankfurt'],
    ICSRM_HDA: ['Hochschule Darmstadt', 'Hochschule Darmstadt'],
    ICSRM_HSRM: ['Hochschule RheinMain', 'Hochschule RheinMain'],
    ICSRM_ICSRM: ['Technische Universität Darmstadt', 'Technische Universität Darmstadt'],
    ICSRM_JGU: ['Johannes Gutenberg-Universität Mainz', 'Johannes Gutenberg-Universität Mainz'],
    ICSRM_ANDERE: ['Andere', 'Other'],

    ICSRM_ARGICULTURE: ['Umwelt-, Agrar- & Forstwissenschaften', 'Agricultural, environmental or forest science'],
    ICSRM_ARCHITECTURE: ['Architektur', 'Architecture'],
    ICSRM_ARTS: ['Kunst, Musik, Design & Mode', 'Arts, Music, Design or Fashion'],
    ICSRM_CIVIL_ENGINEERING: ['Bau- & Umweltingenierwissenschaften', 'Civil & Environmental Engineering'],
    ICSRM_COMPUTER_SCIENCE: ['Informatik', 'Computer Science'],
    ICSRM_ECONOMICS_MANAGEMENT: ['Wirtschaft & Management', 'Economics & Management'],
    ICSRM_ELECTRICAL_ENGINEERING: ['Elektrotechnik', 'Electrical Engineering'],
    ICSRM_ENGINEERING_TECHNOLOGY: ['Ingenieurwesen & Technologie', 'Engineering & Technology'],
    ICSRM_EVENT_MANAGEMENT: ['Event-, Tourismus- & Gastgewerbe-Management', 'Event, Tourism or Hospitality Management'],
    ICSRM_HEALTH: ['Gesundheit & Medizin', 'Health & Medicine'],
    ICSRM_LAW: ['Recht, Steuern & Verwaltung', 'Law, Taxation, Public Administration'],
    ICSRM_LINGUISTICS: ['Linguistik & Kulturwissenschaften', 'Linguistics & Cultural Science'],
    ICSRM_MECHANICAL_ENGINEERING: ['Maschinenbau', 'Mechanical Engineering'],
    ICSRM_MARKETING: ['Medien, Kommunikation & Marketing', 'Media, Communication & Marketing'],
    ICSRM_MATHEMATICS: ['Naturwissenschaften & Mathematik', 'Natural Sciences & Mathematics'],
    ICSRM_PSYCHOLOGY: ['Psychologie', 'Psychology'],
    ICSRM_SOCIAL_SCIENCE: ['Sozialwissenschaften', 'Social Sciences'],
    ICSRM_SPORTS: ['Sport & Fitness', 'Sports & Fitness'],
    ICSRM_PEDAGOGY: ['Lehramt & Pädagogik', 'Teaching & Pedagogy'],
    ICSRM_OTHER: ['Sonstiger Fachbereich', 'Other'],

    ICSRM_BACHELOR: ['Bachelor', 'Bachelor'],
    ICSRM_MASTER: ['Master', 'Master'],
    ICSRM_STAATSEXAMEN_LEHRAMT: ['Staatsexamen Lehramt', 'Staatsexamen Lehramt'],
    ICSRM_STAATSEXAMEN_MEDIZIN_PHARMAZIE: ['Staatsexamen Medizin / Pharmazie', 'Staatsexamen Medizin / Pharmazie'],
    ICSRM_STAATSEXAMEN_JURA: ['Staatsexamen Jura', 'Staatsexamen Jura'],
    ICSRM_PROMOTION: ['Promotion', 'Promotion'],
    ICSRM_SONSTIGE: ['Sonstige', 'Other'],

    ICSRM_MINDESTENS_1_MAL_PRO_WOCHE: ['mindestens einmal pro Woche', 'minimum once a week'],
    ICSRM_ZWEI_MAL_PRO_MONAT: ['zwei mal im Monat', 'twice a month'],
    ICSRM_SELTENER_ALS_ZWEIMAL_PRO_MONAT: ['seltener als zwei mal im Monat', 'less then twice a month'],

    ICSRM_ONLINE_DIGITAL: ['online', 'online'],
    ICSRM_IN_PRAESENZ: ['in Präsenz', 'in person'],
    ICSRM_BEIDES: ['beides', 'both'],

    ICSRM_DEUTSCH: ['Deutsch', 'German'],
    ICSRM_ENGLISCH: ['Englisch', 'English'],
    ICSRM_BOTH: ['beides', 'both'],

    ICSRM_MATCH_MALE: ['Mann', 'Men'],
    ICSRM_MATCH_FEMALE: ['Frau', 'Woman'],
    ICSRM_MATCH_BOTH: ['beides', 'both'],

    ICSRM_KONTAKTE_KNUPFEN: ['Kontakte knüpfen', 'Get to know other People'],
    ICSRM_BRANCHEN_KENNENLERNEN: ['Branchen und Tätigkeitsfelder kennenlernen', 'Get to know other industries'],
    ICSRM_ARBEITSMOEGLICHKEITEN_ERKUNDEN: [
      'Arbeitsmöglichkeiten im Rhein-Main-Gebiet erkunden',
      'discover employment opertunuties in Rhein-Mein area',
    ],
    ICSRM_UNTERSTUEZUNG_DER_KARIEREPLANUNG: [
      'Unterstützung bei der Karriereplanung und -gestaltung',
      'Support in career planing',
    ],
    ICSRM_EINBLICK_ARBEITSALLTAG: ['Einblick in Arbeitsalltag erhalten', 'Get to know every day working life'],
    ICSRM_FACHLICHE_FRAGEN: ['Fachliche Fragen klären', 'technical questions'],
    ICSRM_WORK_LIFE_BALANCE: [
      'Vereinbarkeit von Familie und Beruf „Work-Life-Balance“',
      'Questions about „Work-Life-Balance“',
    ],
    ICSRM_FRAUEN_KARRIEREPLANUNG: ['Frauen Karriereplanung', 'carrer options for woman'],
    ICSRM_KOMMUNIKATIONS_TRAINING: [
      'Training im Bereich Kommunikationsfähigkeiten und Selbstvermarktung',
      'communication training',
    ],
    ICSRM_BEWERBUNGSPROZESS_UNTERTUETZUNG: [
      'Unterstützung im Bewerbungsprozess (Bewerbungsunterlagen/ Interview)',
      'application process support',
    ],
    ICSRM_INTROSPEKTIVE: [
      'Persönliche Stärken und Schwächen erkennen',
      'Get to know your own stregths and weakness better',
    ],
    ICSRM_ALLEINSTELLUNGSMERKMAL_FINDEN: [
      'Alleinstellungsmerkmal/ USP herausarbeiten',
      'find you unique selling point',
    ],
    ICSRM_WEITERBILDUNGSMOEGLICHKEITEN: [
      'Tipps zu Weiterbildungsmöglichkeiten',
      'tips to find opertunities for more specialiced education',
    ],
    ICSRM_ARBEITSTREFFEN_BEGLEITEN: [
      'Mentor/ Mentorin zu Arbeitstreffen begleiten (Meetings, Messen, etc.)',
      'Fllow your mentor in one of his meetings',
    ],
    ICSRM_DEUTSCH_LERNEN: ['Deutsch lernen/ verbessern', 'improve your proficiency in german'],
    // TUDA_ANDERE: ['Andere', 'other'],
  },
  productEntryRequirements: {
    ['ProductEntryRequirementForIcsrmGender']: ['Geschlecht', 'Gender'],
  },
  productWaitinglistPriorityRule: {
    plural: ['Wartelisten Priorität', 'Wartelisten Priority'],
    ['IcsrmUserIsBornInCountryWaitinglistPriorityRule']: ['Nationalität nicht Deutsch', 'Nationality not German'],
    ['IcsrmImmigrationWaitinglistPriorityRule']: ['Zuwanderungsgeschichte vorhanden', 'Immigration history present'],
  },
};

const deutschkurs = {
  // .field = long placeholders used on the registration form; .fieldShort is spread into the top-level field.* namespace for /form-config.
  field: {
    motivationText: [
      'Warum möchtest du an dem Deutschkurs teilnehmen? Was erhoffst du dir davon? (100–200 Wörter)',
      'Why do you want to participate in the German course? What do you hope to gain? (100–200 words)',
    ],
    scheduleText: [
      'Wie sieht dein Zeitplan in einer normalen Woche aus? Wie viele Stunden verbringst du mit Studium und Arbeiten? Wie viel Zeit hast du zum Deutschlernen? (50–150 Wörter)',
      'What does your schedule look like in a normal week? How many hours do you spend on studying and working? How much time do you have to learn German? (50–150 words)',
    ],
    b1CertFileID: ['Deutsch-B1-Zertifikat (PDF)', 'German B1 certificate (PDF)'],
    immaCertFileID: ['Semester-/Immatrikulations-Bescheinigung (PDF)', 'Enrolment certificate (PDF)'],
  },
  fieldShort: {
    motivationText: ['Motivationstext', 'Motivation'],
    scheduleText: ['Wochenplan', 'Weekly schedule'],
    b1CertFileID: ['Deutsch-B1-Zertifikat', 'German B1 certificate'],
    immaCertFileID: ['Semester-/Immatrikulations-Bescheinigung', 'Enrolment certificate'],
    acceptedPlacementTest: ['Bestätigung Einstufungstest', 'Placement test confirmation'],
    acceptedWeeklyTimeCommitment: ['Bestätigung 25h/Woche', '25h/week confirmation'],
    acceptedAttendancePolicy: ['Bestätigung max. 2 Fehltage', 'Max. 2 absences confirmation'],
  },
  label: {
    acceptedPlacementTest: [
      'Ich verstehe, dass ich an einem Einstufungstest teilnehmen muss und erst danach über meine Zulassung zum Deutschkurs entschieden wird.',
      'I understand that I must take a placement test; admission to the German course is decided afterwards.',
    ],
    acceptedWeeklyTimeCommitment: [
      'Ich bestätige, dass ich in der Zeit vom 20.07. bis 09.10.2026 mindestens 25 Stunden pro Woche Zeit für den Deutschkurs sowie zum Selbstlernen habe.',
      'I confirm that between 20 July and 9 October 2026 I have at least 25 hours per week available for the course and self-study.',
    ],
    acceptedAttendancePolicy: [
      'Ich verstehe, dass bei einer Kursteilnahme nur maximal zwei Fehltage erlaubt sind. Bei höheren Fehlzeiten erfolgt der Ausschluss aus dem Kurs.',
      'I understand that at most two absences are allowed during the course. More absences lead to exclusion.',
    ],
  },
  validation: {
    word_count_too_few: ['Zu wenige Wörter.', 'Too few words.'],
    word_count_too_many: ['Zu viele Wörter.', 'Too many words.'],
    accept_required: ['Zustimmung erforderlich.', 'Acceptance required.'],
  },
};

const icsrmFollowUp = {
  isImmigrantExpl: [
    'Ich habe eine Zuwanderungsgeschichte (Mindestens ein Elternteil und/oder ich selbst wurde außerhalb Deutschlands geboren)',
    'at least one of my parents was born outside of Germany',
  ],
  field: {
    email: ['E-Mail', 'Email'],
    firstName: ['Vorname', 'First name'],
    lastName: ['Nachname', 'Last name'],
    isImmigrant: ['Zuwanderungsgeschichte', 'Immigration background'],
    currentUniversity: ['Derzeitige Hochschule', 'Current university'],
    contactConsent: ['Ich bin einverstanden, dass der ICS-RM mich kontaktiert', 'I agree that ICS-RM may contact me'],
  },
  enums: {
    ICSRM_FRA: ['Frankfurt University of Applied Sciences', 'Frankfurt University of Applied Sciences'],
    ICSRM_GU: ['Goethe-Universität Frankfurt', 'Goethe University Frankfurt'],
    ICSRM_HDA: ['Hochschule Darmstadt', 'Darmstadt University of Applied Sciences'],
    ICSRM_HSRM: ['Hochschule RheinMain', 'RheinMain University of Applied Sciences'],
    ICSRM_ICSRM: ['Technische Universität Darmstadt', 'Technische Universität Darmstadt'],
    ICSRM_JGU: ['Johannes Gutenberg-Universität Mainz', 'Johannes Gutenberg University Mainz'],
    ICSRM_ANDERE: ['Andere', 'Other'],
  },
};

const schluesselkompetenzen = {
  field: {
    gender: ['Geschlecht', 'Gender'],
    dateOfBirth: ['Geburtsdatum', 'Date of Birth'],
    nationality: ['Nationalität', 'Nationality'],
    countryOfBirth: ['Geburtsland', 'Country of Birth'],
    uniQualificationCountry: [
      'In welchem Land haben Sie die Hochschulzugangsberechtigung erworben?',
      'In which country have you gained your higher education entrance qualification?',
    ],
    semester: ['Fachsemester', 'Semester'],
    courseOfStudy: ['Studiengang', 'Course of Study'],
    department: ['Fachbereich', 'Department'],
    desiredDegree: ['Angestrebter Abschluss', 'Desired Degree'],
    matriklNr: ['Matrikelnummer', 'matriculation number'],
    newsletter: [
      'Ich möchte den Newsletter erhalten und regelmäßig über Angebote informiert werden.',
      'I would like to receive the newsletter and be regularly informed about upcoming offers.',
    ],
  },
};

const csjensblank = {
  isImmigrantExpl: [
    'Ich habe eine Zuwanderungsgeschichte (Mindestens ein Elternteil und/oder ich selbst wurde außerhalb Deutschlands geboren)',
    'at least one of my parents was born outside of Germany',
  ],
  field: {
    gender: ['Geschlecht', 'Gender'],
    dateOfBirth: ['Geburtsdatum', 'Date of Birth'],
    nationality: ['Nationalität', 'Nationality'],
    countryOfBirth: ['Geburtsland', 'Country of Birth'],
    isImmigrant: ['Zuwanderungsgeschichte', 'Immigration background'],
    uniQualificationCountry: [
      'In welchem Land haben Sie die Hochschulzugangsberechtigung erworben?',
      'In which country have you gained your higher education entrance qualification?',
    ],
    phoneNumber: ['Telefonnummer', 'Phone number'],
    currentUni: ['derzeitige Hochschule', 'current college'],
    semester: ['Fachsemester', 'Semester'],
    courseOfStudy: ['Studiengang', 'Course of Study'],
    department: ['Fachbereich', 'Department'],
    desiredDegree: ['Angestrebter Abschluss', ' desired Degree'],
    streetAndHousenumber: ['Straße und Hausnummer', 'Street'],
    zip: ['PLZ', 'ZIP'],
    city: ['Stadt', 'City'],
    matriklNr: ['Matrikelnummer', 'matriculation number'],
    ichMchteberDenNewsletterberAngeboteInformiertWerden: [
      'Ich möchte den Newsletter des Career Service der Goethe-Universität erhalten und regelmäßig über Recruiting-Veranstaltungen, Stellenangebote, Workshoptermine etc. informiert werden.',
      'I would like to receive the newsletter from the Career Service of Goethe University and be regularly informed about recruiting events, job offers, workshop dates, and similar opportunities.',
    ],
  },
  enums: {
    CSJENSBLANK_MALE: ['männlich', 'male'],
    CSJENSBLANK_FEMALE: ['weiblich', 'female'],
    CSJENSBLANK_DIVERSE: ['divers', 'diverse'],
    CSJENSBLANK_NOT_SPECIFIED: ['ohne Angabe', 'not specfied'],

    CSJENSBLANK_FRA: ['Frankfurt University of Applied Sciences', 'Frankfurt University of Applied Sciences'],
    CSJENSBLANK_GU: ['Goethe-Universität Frankfurt', 'Goethe-Universität Frankfurt'],
    CSJENSBLANK_HDA: ['Hochschule Darmstadt', 'Hochschule Darmstadt'],
    CSJENSBLANK_HSRM: ['Hochschule RheinMain', 'Hochschule RheinMain'],
    CSJENSBLANK_CSJENSBLANK: ['Technische Universität Darmstadt', 'Technische Universität Darmstadt'],
    CSJENSBLANK_JGU: ['Johannes Gutenberg-Universität Mainz', 'Johannes Gutenberg-Universität Mainz'],
    CSJENSBLANK_ANDERE: ['Andere', 'Other'],

    CSJENSBLANK_ARGICULTURE: ['Umwelt-, Agrar- & Forstwissenschaften', 'Agricultural, environmental or forest science'],
    CSJENSBLANK_ARCHITECTURE: ['Architektur', 'Architecture'],
    CSJENSBLANK_ARTS: ['Kunst, Musik, Design & Mode', 'Arts, Music, Design or Fashion'],
    CSJENSBLANK_CIVIL_ENGINEERING: ['Bau- & Umweltingenierwissenschaften', 'Civil & Environmental Engineering'],
    CSJENSBLANK_COMPUTER_SCIENCE: ['Informatik', 'Computer Science'],
    CSJENSBLANK_ECONOMICS_MANAGEMENT: ['Wirtschaft & Management', 'Economics & Management'],
    CSJENSBLANK_ELECTRICAL_ENGINEERING: ['Elektrotechnik', 'Electrical Engineering'],
    CSJENSBLANK_ENGINEERING_TECHNOLOGY: ['Ingenieurwesen & Technologie', 'Engineering & Technology'],
    CSJENSBLANK_EVENT_MANAGEMENT: [
      'Event-, Tourismus- & Gastgewerbe-Management',
      'Event, Tourism or Hospitality Management',
    ],
    CSJENSBLANK_HEALTH: ['Gesundheit & Medizin', 'Health & Medicine'],
    CSJENSBLANK_LAW: ['Recht, Steuern & Verwaltung', 'Law, Taxation, Public Administration'],
    CSJENSBLANK_LINGUISTICS: ['Linguistik & Kulturwissenschaften', 'Linguistics & Cultural Science'],
    CSJENSBLANK_MECHANICAL_ENGINEERING: ['Maschinenbau', 'Mechanical Engineering'],
    CSJENSBLANK_MARKETING: ['Medien, Kommunikation & Marketing', 'Media, Communication & Marketing'],
    CSJENSBLANK_MATHEMATICS: ['Naturwissenschaften & Mathematik', 'Natural Sciences & Mathematics'],
    CSJENSBLANK_PSYCHOLOGY: ['Psychologie', 'Psychology'],
    CSJENSBLANK_SOCIAL_SCIENCE: ['Sozialwissenschaften', 'Social Sciences'],
    CSJENSBLANK_SPORTS: ['Sport & Fitness', 'Sports & Fitness'],
    CSJENSBLANK_PEDAGOGY: ['Lehramt & Pädagogik', 'Teaching & Pedagogy'],
    CSJENSBLANK_OTHER: ['Sonstiger Fachbereich', 'Other'],

    CSJENSBLANK_BACHELOR: ['Bachelor', 'Bachelor'],
    CSJENSBLANK_MASTER: ['Master', 'Master'],
    CSJENSBLANK_STAATSEXAMEN_LEHRAMT: ['Staatsexamen Lehramt', 'Staatsexamen Lehramt'],
    CSJENSBLANK_STAATSEXAMEN_MEDIZIN_PHARMAZIE: [
      'Staatsexamen Medizin / Pharmazie',
      'Staatsexamen Medizin / Pharmazie',
    ],
    CSJENSBLANK_STAATSEXAMEN_JURA: ['Staatsexamen Jura', 'Staatsexamen Jura'],
    CSJENSBLANK_PROMOTION: ['Promotion', 'Promotion'],
    CSJENSBLANK_SONSTIGE: ['Sonstige', 'Other'],

    CSJENSBLANK_MINDESTENS_1_MAL_PRO_WOCHE: ['mindestens einmal pro Woche', 'minimum once a week'],
    CSJENSBLANK_ZWEI_MAL_PRO_MONAT: ['zwei mal im Monat', 'twice a month'],
    CSJENSBLANK_SELTENER_ALS_ZWEIMAL_PRO_MONAT: ['seltener als zwei mal im Monat', 'less then twice a month'],

    CSJENSBLANK_ONLINE_DIGITAL: ['online', 'online'],
    CSJENSBLANK_IN_PRAESENZ: ['in Präsenz', 'in person'],
    CSJENSBLANK_BEIDES: ['beides', 'both'],

    CSJENSBLANK_DEUTSCH: ['Deutsch', 'German'],
    CSJENSBLANK_ENGLISCH: ['Englisch', 'English'],
    CSJENSBLANK_BOTH: ['beides', 'both'],

    CSJENSBLANK_MATCH_MALE: ['Mann', 'Men'],
    CSJENSBLANK_MATCH_FEMALE: ['Frau', 'Woman'],
    CSJENSBLANK_MATCH_BOTH: ['beides', 'both'],

    CSJENSBLANK_KONTAKTE_KNUPFEN: ['Kontakte knüpfen', 'Get to know other People'],
    CSJENSBLANK_BRANCHEN_KENNENLERNEN: ['Branchen und Tätigkeitsfelder kennenlernen', 'Get to know other industries'],
    CSJENSBLANK_ARBEITSMOEGLICHKEITEN_ERKUNDEN: [
      'Arbeitsmöglichkeiten im Rhein-Main-Gebiet erkunden',
      'discover employment opertunuties in Rhein-Mein area',
    ],
    CSJENSBLANK_UNTERSTUEZUNG_DER_KARIEREPLANUNG: [
      'Unterstützung bei der Karriereplanung und -gestaltung',
      'Support in career planing',
    ],
    CSJENSBLANK_EINBLICK_ARBEITSALLTAG: ['Einblick in Arbeitsalltag erhalten', 'Get to know every day working life'],
    CSJENSBLANK_FACHLICHE_FRAGEN: ['Fachliche Fragen klären', 'technical questions'],
    CSJENSBLANK_WORK_LIFE_BALANCE: [
      'Vereinbarkeit von Familie und Beruf „Work-Life-Balance"',
      'Questions about „Work-Life-Balance"',
    ],
    CSJENSBLANK_FRAUEN_KARRIEREPLANUNG: ['Frauen Karriereplanung', 'carrer options for woman'],
    CSJENSBLANK_KOMMUNIKATIONS_TRAINING: [
      'Training im Bereich Kommunikationsfähigkeiten und Selbstvermarktung',
      'communication training',
    ],
    CSJENSBLANK_BEWERBUNGSPROZESS_UNTERTUETZUNG: [
      'Unterstützung im Bewerbungsprozess (Bewerbungsunterlagen/ Interview)',
      'application process support',
    ],
    CSJENSBLANK_INTROSPEKTIVE: [
      'Persönliche Stärken und Schwächen erkennen',
      'Get to know your own stregths and weakness better',
    ],
    CSJENSBLANK_ALLEINSTELLUNGSMERKMAL_FINDEN: [
      'Alleinstellungsmerkmal/ USP herausarbeiten',
      'find you unique selling point',
    ],
    CSJENSBLANK_WEITERBILDUNGSMOEGLICHKEITEN: [
      'Tipps zu Weiterbildungsmöglichkeiten',
      'tips to find opertunities for more specialiced education',
    ],
    CSJENSBLANK_ARBEITSTREFFEN_BEGLEITEN: [
      'Mentor/ Mentorin zu Arbeitstreffen begleiten (Meetings, Messen, etc.)',
      'Fllow your mentor in one of his meetings',
    ],
    CSJENSBLANK_DEUTSCH_LERNEN: ['Deutsch lernen/ verbessern', 'improve your proficiency in german'],
  },
  /* Commented out until corresponding classes are created
  productEntryRequirements: {
    [ProductEntryRequirementForCsjensblanksGender._class]: ['Geschlecht', 'Gender'],
  },
  productWaitinglistPriorityRule: {
    plural: ['Wartelisten Priorität', 'Wartelisten Priority'],
    [CsjensblanksUserIsBornInCountryWaitinglistPriorityRule._class]: ['Nationalität nicht Deutsch', 'Nationality not German'],
    [CsjensblanksImmigrationWaitinglistPriorityRule._class]: [
      'Zuwanderungsgeschichte vorhanden',
      'Immigration history present',
    ],
  },
  */
};

const buildYourOwnAi = {
  field: {
    availability: ['Ihre Verfügbarkeit am Veranstaltungstag', 'Your availability at the event'],
    role: ['Rolle', 'Role'],
  },
  enums: {
    BUILD_YOUR_OWN_AI_STUDENT: ['Student*in', 'Student'],
    BUILD_YOUR_OWN_AI_MENTOR: ['Lehrende', 'Teacher'],
    BUILD_YOUR_OWN_AI_MORNING: ['nur vormittags', 'morning only'],
    BUILD_YOUR_OWN_AI_AFTERNOON: ['nur nachmittags', 'afternoon only'],
    BUILD_YOUR_OWN_AI_WHOLE_DAY: ['ganztägig', 'full day'],
  },
};

// TODO need to change engine tiptap module to avoid copy pasting all translations
const tiptapTranslations = {
  tiptap: {
    bold: { tooltip: ['Fett', 'Bold'] },
    italic: { tooltip: ['Kursiv', 'Italic'] },
    underline: { tooltip: ['Unterstrichen', 'Underlined'] },
    strike: { tooltip: ['Durchgestrichen', 'Strike'] },
    textAlign: {
      tooltip: {
        alignLeft: ['Linksbündig', 'Align left'],
        alignCenter: ['Zentriert', 'Align center'],
        alignRight: ['Rechtsbündig', 'Align right'],
        justify: ['Blocksatz', 'Justify'],
      },
    },
    link: {
      tooltip: {
        linkAdd: ['Link einfügen', 'Add link'],
        linkRemove: ['Link entfernen', 'Remove Link'],
      },
    },
    heading: {
      heading1: ['Überschrift 1', 'Heading 1'],
      heading2: ['Überschrift 2', 'Heading 2'],
      heading3: ['Überschrift 3', 'Heading 3'],
      heading4: ['Überschrift 4', 'Heading 4'],
    },
    highlight: {
      tooltipHighlight: ['Text Markieren', 'Highlight text'],
      tooltipHighlightError: ['Fehler Markieren', 'Highlight error'],
      tooltipHighlightRemove: ['Markierung entfernen', 'Remove highlight'],
    },
  },
};

export const translations = {
  pageTitle: {
    action: ['Aktion', 'Action'],
    passwordReset: ['Passwort zurücksetzen', 'Reset password'],
    registration: ['Registrierung', 'Registration'],
    product: ['Veranstaltung', 'Event'],
    support: ['Support', 'Support'],
    participation: ['Teilnahme bestätigen', 'Confirm attendance'],
    institutionSelection: ['Einrichtung auswählen', 'Select institution'],
    participant: ['Teilnehmer:in', 'Participant'],
    lecturer: ['Dozent:in', 'Lecturer'],
    workshopNotes: ['Notizen', 'Notes'],
    bundleAdd: ['Bundle anlegen', 'Create bundle'],
    bundleEdit: ['Bundle bearbeiten', 'Edit bundle'],
    workshopSetAdd: ['Workshop-Reihe anlegen', 'Create workshop series'],
    workshopSetEdit: ['Workshop-Reihe bearbeiten', 'Edit workshop series'],
    whatsapp: ['WhatsApp-Konfiguration', 'WhatsApp configuration'],
    maintenance: ['Wartungsmodus', 'Maintenance mode'],
    institutionOverview: ['Institutionsübersicht', 'Institution overview'],
  },

  fieldInfo: fieldInfoTranslations,

  ...dataViewTranslations,
  ...filterTranslations,
  ...tiptapTranslations,
  ...(fieldManagementTranslations as any as Record<string, never>), // add these translations without typecast make (typeof translations) = any; Maybe it is not used properly?
  ...samlFederationTranslations,

  error: ['Fehler', 'Error'],
  save: ['Speichern', 'Save'],
  revert: ['Rückgängig machen', 'Revert'],
  edit: ['Bearbeiten', 'Edit'],
  saved: ['Gespeichert', 'Saved'],
  copied: ['Kopiert', 'Copied'],
  delete: ['Löschen', 'Delete'],
  deleted: ['gelöscht', 'deleted'],
  activate: ['aktivieren', 'Activate'],
  add: ['hinzufügen', 'Add'],
  deactivate: ['deaktivieren', 'Deactivate'],
  create: ['Erstellen', 'Create'],
  sync: ['synchronisieren', 'Synchronise'],
  copy: ['Kopieren', 'Copy'],
  skip: ['Überspringen', 'Skip'],
  search: ['Suchen', 'Search'],
  export: ['Exportieren', 'Export'],
  override: ['überschreibe', 'override'],
  copiedLink: ['Link in die Zwischenablage kopiert.', 'Copied link to clipboard.'],
  error_saving: ['Es gab ein Problem beim Speichern', 'Error while saving'],
  loading: ['Lädt...', 'Loading...'],
  saving: ['wird gespeichert...', 'is saving...'],
  syncing: ['wird synchronisiert...', 'is synchronising...'],
  no_results: ['Keine Ergebnisse gefunden', 'No results'],
  show_more: ['Mehr anzeigen', 'Show more'],
  show_less: ['Weniger anzeigen', 'Show less'],
  copied_to_clipboard: ['In die Zwischenablage kopiert!', 'Copied to clipoard'],
  change_language: ['Sprache ändern', 'Change language'],
  request: ['Anfordern', 'Request'],
  generate: ['Generieren', 'Generate'],
  select: ['Auswählen', 'Select'],
  discard: ['Abbrechen', 'Discard'],
  approve: ['Bestätigen', 'Approve'],
  suspend: ['Suspendieren', 'Suspend'],
  finish: ['Weiter zur Übersicht', 'Continue to overview'],
  verify: ['überprüfen', 'Verify'],
  confirm: ['Bestätigen', 'Confirm'],
  contact: ['Kontaktieren', 'Contact'],
  yes: ['Ja', 'Yes'],
  no: ['Nein', 'No'],
  back: ['Zurück', 'Back'],
  next: ['Weiter', 'Next'],
  preview: ['Vorschau', 'Preview'],
  cancel: ['Abbrechen', 'Cancel'],
  cancelled_payment: [
    'Bezahlvorgang wurde abgebrochen. Bitte versuchen Sie es erneut oder gehen Sie zurück zur Übersicht',
    'Payment process was cancelled. Please try again or return to overview',
  ],
  processing_booking: ['Buchung wird verarbeitet...', 'Processing booking...'],
  loading_payments: ['Zahlung wird vorbereitet...', 'Loading payments...'],
  place: ['Platz', 'Place'],

  on: ['Am', 'On'],
  from: ['Von', 'From'],
  till: ['Bis', 'Till'],
  soon: ['bald', 'soon'],
  available: ['verfügbar', 'available'],
  bookable_from: ['buchbar ab', 'bookable from'],
  and: ['und', 'and'],

  seconds: ['Sekunden', 'seconds'],
  minutes: ['Minuten', 'minutes'],
  hours: ['Stunden', 'hours'],
  days: ['Tage', 'days'],
  uhr: ['Uhr', ' '],

  dashboard: {
    greeting_morning: ['Guten Morgen', 'Good morning'],
    greeting_afternoon: ['Guten Tag', 'Good afternoon'],
    greeting_evening: ['Guten Abend', 'Good evening'],
    new_workshop: ['Neuer Workshop', 'New workshop'],
    manage_bookings: ['Workshops verwalten', 'Manage workshops'],
    next_workshops: ['Nächste Workshops', 'Upcoming workshops'],
    show_all: ['Alle anzeigen', 'Show all'],
    no_upcoming: ['Keine anstehenden Workshops', 'No upcoming workshops'],
    workshops_unavailable: [
      'Die nächsten Workshops konnten nicht geladen werden.',
      'The upcoming workshops could not be loaded.',
    ],
    calendar_subscribe: ['Kalender abonnieren', 'Subscribe to calendar'],
    calendar_subscribe_info: [
      'Alle Termine automatisch in Ihrem Kalender — Änderungen werden laufend übernommen.',
      'All dates automatically in your calendar — changes are applied continuously.',
    ],
    subscribe: ['Jetzt abonnieren', 'Subscribe now'],
    copy: ['Kopieren', 'Copy'],
    copied: ['Kopiert ✓', 'Copied ✓'],
    copy_failed: ['Der Link konnte nicht kopiert werden.', 'The link could not be copied.'],
    calendar_unavailable: [
      'Der Kalender-Link ist derzeit nicht verfügbar.',
      'The calendar link is currently unavailable.',
    ],
    calendar_howto_title: [
      'Anleitung um einen Kalender per ICS zu abonnieren.',
      'How to subscribe to a calendar via ICS.',
    ],
    this_month: ['Diesen Monat', 'This month'],
    stat_workshops: ['Workshops', 'Workshops'],
    stat_occupied: ['Belegte Plätze', 'Occupied places'],
    stat_new_bookings: ['Neue Buchungen', 'New bookings'],
    occupied: ['belegt', 'occupied'],
    stats_unavailable: [
      'Die Monatsstatistik konnte nicht geladen werden.',
      'The monthly statistics could not be loaded.',
    ],
    quick_links: ['Schnellzugriff', 'Quick links'],
    institution_unavailable: ['Die Einrichtung konnte nicht geladen werden.', 'The institution could not be loaded.'],
  },

  mail_templates: ['E-Mail-Templates', 'Mail-Templates'],
  [InstitutionLegalDoc._class]: {
    privacy: ['Datenschutz', 'Privacy policy'],
    'terms-of-use': ['Nutzungsbedingungen', 'Terms of use'],
    accessibility: ['Barrierefreiheit', 'Accessibility'],
    imprint: ['Impressum', 'Imprint'],
    not_available: ['Dieses Dokument ist nicht verfügbar.', 'This document is not available.'],
  },

  product_page: ['Produktseite', 'Product page'],

  actions: {
    link_received: ['Sie haben einen neuen Link erhalten', 'You received a link'],
    information_warning: [
      'Geben Sie diesen unter keinen Umständen an dritte weiter',
      'Dont disclose this information to a third party under any circumstances',
    ],
    back_to_login: ['Zurück zur Anmeldung', 'back to login'],
    that_worked: ['Das hat geklappt', 'that worked'],
    password_change_success: [
      'Sie haben erfolgreich Ihr Passwort geändert. Sie können sich nun anmelden.',
      'You succesfully changed your password. You are now able to login',
    ],
    to_login: ['Weiter zur Anmeldung', 'Login now'],
    inp_password: ['Geben Sie ein Passwort an.', 'Enter a password'],
    pwd_err_identical: ['Die Passwörter müssen übereinstimmen.', 'Please insert matching passwords.'],
    pwd_err_length: [
      'Das Passwort muss mindestens 8 Zeichen lang sein, einen Groß- und einen Kleinbuchstaben, sowie eine Zahl und ein Sonderzeichen beinhalten',
      'Password must contain atleast 8 characters, one uppercase letter, one lowercase, one number and one special character',
    ],
    link_already_used: [
      'Es scheint so, als wurde dieser Link schonmal genutzt.',
      'It seams like this links was used already',
    ],
    link_already_used_2: ['Bitte fordern Sie einen neuen Link an.', 'Please request a new link'],
    user_not_found: ['Nutzer konnte nicht gefunden werden', 'User could not be found'],
    invalid_link: ['Ungültiger Link.', 'Invalid link'],
    to_homepage: ['Weiter zur Startseite', 'continue to homepage'],
    getting_logged_in: ['Sie werden eingeloggt...', 'Your are getting logged in...'],
    retry_payment: ['Bezahlvorgang erneut starten', 'Restart payment process'],
    deregister_title: ['Abmeldung', 'Deregistration'],
    deregister_confirm: [
      'Möchten Sie sich von der folgenden Veranstaltung abmelden?',
      'Would you like to deregister from the following event?',
    ],
    deregister_cancel: ['Abbrechen', 'Cancel'],
    deregister_submit: ['Jetzt abmelden', 'Deregister now'],
    deregister_success_title: ['Abmeldung erfolgreich', 'Deregistration successful'],
    deregister_success_message: ['Sie wurden erfolgreich abgemeldet.', 'You have been successfully deregistered.'],
    deregister_error_title: ['Abmeldung fehlgeschlagen', 'Deregistration failed'],
    deregister_error_signoff_blocked: [
      'Die Abmeldefrist ist abgelaufen. Bitte kontaktieren Sie uns per E-Mail.',
      'The deregistration deadline has passed. Please contact us via email.',
    ],
    to_profile: ['Zum Profil', 'Go to profile'],
    newsletter_optin_success_title: ['Newsletter abonniert', 'Newsletter subscribed'],
    newsletter_optin_success_message: [
      'Sie haben den Newsletter erfolgreich abonniert.',
      'You have successfully subscribed to the newsletter.',
    ],

    no_institution_error: ['Keine Institution gefunden.', "Didn't find any institution"],
    invalid_data_error: ['Ungültige Daten.', 'Invalid data'],
    invalid_link_error: ['Ungültiger Link.', 'Invalid link'],
  },

  settings: {
    name: ['Einstellungen', 'Settings'],
    info: [
      'Änderung von Einstellungen, die für die gesamte Einrichtung gelten. Datenschutz-Texte, Fristen, etc.',
      'Change settings that apply to the entire facility. Data protection texts, deadlines, etc.',
    ],
    no_file: ['Keine Datei hochgeladen', 'no file uploaded'],
    del_crt_cnf: ['Wollen Sie dieses Zertifikat wirklich löschen?', 'Do you really want to delete this certificate'],
    embed_form_guide: ['Formular-Einbinden-Guide', 'embed-form-guide'],
    ov_institution: ['Einrichtungsübersicht', 'Institution overview'],
    ov_workshop: ['Workshop-Übersicht', 'Workshop overview'],
    ov_single_workshop: ['Einzelworkshop', 'Single-Workshop'],
    embed_tut_1: [
      'Um ein Anmeldeformular für einzelne Workshops einzubinden, wählen Sie hier den Workshop aus für den das Anmeldeformular generiert werden soll.',
      'Select a workshop, to embed it.',
    ],
    embed_tut_2: [
      'Für Einzelberatungen können aktuell keine Einzelanmeldung-Formulare generiert werden!',
      'At the moment it is not possible to generate forms for appointments!',
    ],
    embed_as_webcmp: ['Als Web-Component einbinden', 'embed as webcomponent'],
    embed_as_webcmp_exp: [
      'Bei dieser Variante binden Sie das Formular als Web-Component ein, dadurch müssen Sie sich um Höhe und Breite im Regelfall keine Gedanken machen, da sich das Component diese selbst nehmen kann.',
      "With this method you are embedding the form as Web-Component, therefore you don't have to think about setting a width or height, because the component will most likely choose that on its own.",
    ],
    wrn_no_workshop_selected: [
      'Um ein Anmeldeformular direkt für Workshops generieren zu können, muss zuerst ein Workshop ausgewählt werden!',
      'You have to first select a Workshop to be able to generate a form for it',
    ],
    insert_script: ['Scripts einfügen', 'insert script'],
    insert_script_desc: [
      'Fügen Sie folgenden Code in den Body Ihrer HTML-Seite ein',
      'Copy and past following code into the Body of your html-page',
    ],
    embed_as_iframe: ['Als IFrame einbinden', 'Embed as IFrame'],
    embed_as_iframe_desc: [
      'Hier wird das Formular als iframe auf Ihrer Seite eingebunden, die Höhe und Breite müssen Sie dabei selbst anpassen, da die Höhe des Inhalts während des Anmeldeprozesses variieren kann, kann es vorkommen, dass das iframe einen Scroll-Balken anzeigt, daher ist die Web-Component Variante immer zu bevorzugen.',
      'With this method the form gets embedded as IFrame. You will have to adjust the height and with of the component yourself. It is possible that the IFrame shows a scrolling-bar during the login process. Because of that it is recommend to use a webcomponed instead, if possible',
    ],
    contact_adm_to_embed: [
      'Bitte wenden Sie sich an Ihren Super-Admin zur Einbindung des Anmeldeformulars',
      'Please contact you super-admin to embed the login-form',
    ],
    iframe_embed_not_working: [
      'Auch die Einbindung als IFrame funktioniert nicht',
      "Embedding the IFrame also doesno't work",
    ],
    webcmp_embed_not_working: [
      'Die Einbindung mit dem Web-Component funktioniert nicht',
      "Embeding the Web-Component doesn't work",
    ],
    general_information: ['Allgemeine Informationen', 'General information'],
    basic_information: ['Basis Informationen', 'Basic information'],
    copy_url: ['URL kopieren', 'Copy URL'],
    open_url: ['Im neuen Tab öffnen', 'Open in new tab'],
    upload_image: ['Bild hochladen', 'upload Image'],
    short_description: ['Kurz-Beschreibung', 'Short-Description'],
    ccMail: ['Cc E-Mail', 'Cc E-Mail'],
    sort_by_date: ['Termine in Registrierung nach Datum sortieren', 'Sort appointments by date'],
    mail_addresses: ['Mail Adressen', 'Mail Addresses'],
    cc_confirmation: ['CC-Bestätigungsadresse', 'CC-Confirmation address'],
    sender_address: ['Absender-Adresse', 'sender-address'],
    contact_mail: ['Kontakt E-Mail', 'Contact E-Mail'],
    mail_signature: ['E-Mail Signatur', 'E-Mail Signatur'],
    workshop_info: ['Workshop Informationen', 'Workshop info'],
    config_homepage_hints: [
      'Hinweis für die Einrichtungsstartseite (über der Workshopliste)',
      'Hints for the config-homepage (above the workshop list)',
    ],
    default_value_expl: [
      'Dies dient als Standard-Wert. Kann in den Terminen selbst definiert werden.',
      'Here you can set default values. They can be overridden in the appointment/workshop settings.',
    ],
    course_location: ['Ort der Veranstaltung', 'Course Location'],
    cancel_deadline: ['Abmeldefrist (in Tagen)', 'Cancelation notice period (in days)'],
    move_up_notice: ['Nachrück-Blockfrist (in Tagen)', 'Move up notice (in days)'],
    block_sign_in_hours: ['Anmeldefrist (in Stunden)', 'Registration deadline (in hours)'],
    active_registration_deadline: ['Aktive Anmeldefrist (in Stunden)', 'Active registration deadline (in hours)'],
    reminder_mail_1: ['1. Erinnerungsmail (Tage zuvor)', '1. mail reminder (in days before)'],
    reminder_mail_2: ['2. Erinnerungsmail (Tage zuvor)', '2. mail reminder (in days before)'],
    einzelberatung_meeting_info_lead_time: [
      'Meeting-Info-Mail zur Einzelberatung (Tage zuvor)',
      'Counseling meeting info mail (days before)',
    ],
    appointment_reminder_mail_1: [
      '1. Termin-Erinnerungsmail (Tage zuvor)',
      '1. appointment mail reminder (in days before)',
    ],
    appointment_reminder_mail_2: [
      '2. Termin-Erinnerungsmail (Tage zuvor)',
      '2. appointment mail reminder (in days before)',
    ],
    appointment_info_mail: [
      '1. Infomail mit Termininfos (Tage zuvor)',
      '1st info mail with appointment details (days before)',
    ],
    appointment_info_mail_2: [
      '2. Infomail mit Termininfos (Stunden zuvor)',
      '2nd info mail with appointment details (hours before)',
    ],
    qrcode_valid: [
      'Wie viele Minuten soll der QRCode nach Terminende gültig sein?',
      'How long should the QRcode be valid for after the Appointment ended?',
    ],
    max_simul_reg: ['Maximale gleichzeitige Anmeldungen', 'Maximum simultaniously registrations'],
    maintainance_mode: ['Wartungsmodus', 'Maintainance mode'],
    act_maintainance_mode: ['Wartungsmodus aktivieren', 'activate Maintainance mode'],
    when_maintainance_on: [
      'Dies wird angezeigt, wenn der Wartungsmodus aktiv ist.',
      'The following will be shown when the maintainance mode is active.',
    ],
    err_mail_banner: [
      'Es gab ein Problem beim Aktualisieren des Mail Banner Bildes. Bitte versuchen Sie es später erneut!',
      'There was an error while refreshing the mail-banner-image. Please try again later.',
    ],
    override_mail_msg: [
      'E-Mail Vorlagen können hier für die ausgewählte Einrichtung überschrieben werden. Institutionsspezische E-Mail Vorlagen werden erst beim ersten Speichern erstellt. Die globalen E-Mail-Vorlagen werden beim Erstellen einer neuen Einrichtung kopiert!',
      'E-Mail templates can be overridden on an institution basis. Templates specific to the respective institution will only be created once the template has been initially saved. The global e-mail templates will be copied to all new institutions!',
    ],
    config_mail_banner_image: ['Banner-Bild für E-Mails einrichten', 'config banner-image for e-mails'],
    upload_mail_banner_image: [
      'Hier kann ein Banner Bild für alle Mails hochgeladen werden, das immer ganz oben in den Mails angezeigt wird.',
      'Here you can upload the banner-image. It will be shown at the top of e-mails.',
    ],
    confirm_del: ['Wollen Sie diese Einrichtung wirklich löschen?', 'Do you really want to delete this institution?'],
    manage_crts: ['Bescheinigungen & Zertifikate verwalten', 'manage certificates'],
    manage_contracts: ['Verträge verwalten', 'manage contracts'],
    edit_mail_templates: ['E-Mail-Templates bearbeiten', 'edit e-mail templates'],
    define_colors: ['Farben definieren', 'define colors'],
    define_product_page: ['Produktseite bearbeiten...', 'Edit product page...'],
    confirm_stammdaten_field_del: [
      'Wollen Sie dieses Feld wirklich aus den Stammdaten entfernen?',
      'Do you really want to remove this custom field',
    ],
    confirm_field_del: ['Wollen Sie dieses Feld wirklich löschen?', 'Do you really want to remove this field'],

    defaults: {
      workshop: ['Workshop', 'Workshop'],
      institution: ['Institutionsstandard', 'Institution standart'],
      global: ['Globaler Standard', 'Global Standart'],
    },
    internAccountingEmail: ['Interne Buchhaltungs-Email', 'Intern Accounting-Email'],
    ingeritedInternAccountingEmail: ['Interne Buchhaltungs-Email (abgeleitet)', 'Intern Accounting-Email (derived)'],
    internAccountingEmailDesc: [
      'An diese E-Mail werden interne Rechnungskopien geschickt',
      'Interal invoice-copies will be sent to this address',
    ],
    sendInvoiceDaysInAdvance: [
      'Die Rechnung wird x Tage vor Workshop Beginn versendet',
      'Number of Days the Invoice will get send befor the workshop starts',
    ],
    extraParticipationConstraintsExpl: [
      'Tragen Sie hier alle Voraussetzungen für das Bestehen der Veranstaltung als kommaseparierte Liste ein. Diese erscheinen als Checkboxen in der Teilnehmerliste und können manuell abgehakt werden.',
      'Insert all requirements for passing this Workshop as comma separated list. Each item will appear as checkbox in the participation list.',
    ],
  },

  imp_exp: {
    exp_sd_invoices: ['Rechnungen exportieren (CSV)', 'Export invoices (CSV)'],
    name: ['Export', 'Export'],
    products: ['Produkte', 'Products'],
    users: ['Nutzer', 'Users'],
    participations: ['Teilnehmer', 'Participations'],
    err_exp_news_adr: [
      'Es gab ein Problem beim Exportieren der Newsletter-Adressen',
      'There was an error while exporting the newsletter-addresses',
    ],
    exp_reg_list: ['Anmelde-Liste exportieren', 'Export registration-list'],
    exp_reg_list_desc: [
      'Alle Anmeldungen dieser Einrichtung, inklusive des Anmeldestatus, exportieren.',
      'Export all registration of this institution, including the regisration-state',
    ],
    err_exp_reg_list: [
      'Es gab ein Problem beim Importieren der Anmelde-Liste',
      'There was an error while importing the registration-list',
    ],
    imp_reg_list: ['Anmelde-Liste importieren', 'Import registration-list'],
    imp_reg_list_desc: [
      'Anmeldungen in diese Einrichtung importieren. Anmeldungen die nicht explizit überschrieben werden bleiben dabei erhalten. Es können nur bereits bestehende Anmeldungen bearbeitet werden. Änderungen an Nutzer-Daten oder Workshop-Daten bleiben unbeachtet.',
      "Import registrations in this institution. Registrations which are not explicitly overriden won't be deleted. Only already available registrations can be edited. Changes within user-data or workshop-data remain unnoticed",
    ],
    err_exp_participants: [
      'Es gab ein Problem beim Exportieren der Teilnehmer',
      'There was an error while exporting the participations',
    ],
    exp_participants: ['Teilnehmer exportieren', 'export participants'],
    exp_participants_desc: ['Alle Nutzer dieser Einrichtung exportieren.', 'export all users in this institution'],
    err_exp_workshops: [
      'Es gab ein Problem beim Exportieren der Workshops',
      'There was an error while exporting the workshops',
    ],
    exp_workshops: ['Workshops exportieren', 'export workshops'],
    exp_workshops_desc: ['Alle Workshops dieser Einrichtung exportieren.', 'Export all workshops of this institution'],
    imp_workshops: ['Workshops importieren', 'import workshops'],
    imp_workshops_desc: ['Workshops in diese Einrichtung importieren.', 'Import workshops into this instituition'],
    export_everything: ['Daten exportieren', 'Export data'],
    exp_intern_payments: ['interne Zahlungen exportieren', 'export internal Payments'],
    exp_receiver_counts_per_month: ['Intern/Extern pro Monat exportieren', 'Export internal/external per month'],
    exp_participations: ['Anmeldungen exportieren', 'export participations'],
    no_data_found: ['Es wurden keine Daten zum exportieren gefunden', 'No data to export found'],
  },

  termin: {
    singular: ['Termin', 'Appointment'],
    plural: ['Termine', 'appointments'],
    all_termins: ['Alle Termine', 'all appointments'],
    dateNotYetDetermined: ['Datum steht noch nicht fest', 'Date is yet to be determined'],
  },

  statistics: {
    name: ['Statistiken', 'Statistics'],
    info: [
      'Einsicht von Statistiken, die für die gesamte Einrichtung gelten. Anzahl Workshops, User Teilnahmen, etc.',
      'Insight into statistics that apply to the entire facility. Number of workshops, user participation, etc.',
    ],
    name_inst: ['Einrichtungsweite Statistiken', 'Institution statistics'],
    reg_participants: ['Registrierte Teilnehmende', 'registered participants'],
    total_workshops: ['Angelegte Workshops (Gesamt)', 'workshops created (total)'],
    appointments_attended: ['Termine wahrgenommen', 'Appointments attended'],
    appointments_total: ['in insgesamt Terminen', 'in total appointments'],
    active_and_completed_participations: ['Aktive und erfolgreiche Teilnahmen', 'active and completed participations'],
    active_participations: ['Aktive Teilnahmen', 'active participations'],
    completed_participations: ['Erfolgreiche Teilnahmen', 'completed participations'],
    cancled_and_suspened_and_waiting_participations: [
      'Stornierte, gesperrte und wartende Teilnahmen',
      'canceled, suspended and waiting participations',
    ],
    suspened_participations: ['Suspendierte Teilnahmen', 'suspended participations'],
    cancled_participations: ['Stornierte Teilnahmen', 'canceled participations'],
    waiting_participations: ['Wartende Teilnahmen', 'waiting participations'],
    no_show_participations: ['Nicht erschienene Teilnahmen', 'no-show participations'],
    average_participating_and_participated_participations_per_user: [
      'Durchschnittliche aktive und erfolgreiche Teilnahmen pro Nutzer',
      'Average active and completed participations per user',
    ],
    average_suspended_and_signed_off_and_waiting_participations_per_user: [
      'Durchschnittliche gesperrte, suspenierte und wartende Teilnahmen pro Nutzer',
      'Average suspended, canceled and waiting participations per user',
    ],
    average_suspended_participations_per_user: [
      'Durchschnittliche gesperrte Teilnahmen pro Nutzer',
      'Average suspended participations per user',
    ],
    average_signed_off_participations_per_user: [
      'Durchschnittliche stornierte Teilnahmen pro Nutzer',
      'Average canceled participations per user',
    ],
    average_waiting_participations_per_user: [
      'Durchschnittliche wartende Teilnahmen pro Nutzer',
      'Average waiting participations per user',
    ],
    average_no_show_participations_per_user: [
      'Durchschnittliche nicht erschienene Teilnahmen pro Nutzer',
      'Average no-show participations per user',
    ],
    average_participations_per_user: ['Durchschnittliche Teilnahmen pro Nutzer', 'Average participations per user'],
    user_over_time: ['Nutzer Registrierungen über Zeit', 'Users registartions over time'],
    product_registrations_over_time: ['Produkt Registrierungen über Zeit', 'Product registrations over time'],
    workshop_participation_over_time: ['Produkt Teilnahmen über Zeit', 'Product participations over time'],
    participation_count_per_bookingstate: [
      'Teilnehmerzahlen nach Buchungsstatus',
      'Participation count per booking-state',
    ],
    reg_institutions: ['Einrichtungen registriert', 'registered institutions'],
    reg_admins: ['Einrichtungs-Admins registriert', 'registered institution-admins'],
    icsrm_gender_distribution: ['Geschlechterverteilung', 'Gender distribution'],
    icsrm_daad_country_disribution: ['Herkunftsländer', 'Country of origin'],
    icsrm_citizenship_disribution: ['Nationalitäten', 'nationality'],
    icsrm_uni_disribution: ['Universitäten', 'University'],
    icsrm_desired_degree_disribution: ['angestrebter Abschluss', 'desied degree'],
    icsrm_participation_count_per_timefrmae_and_immigration_history: [
      'Teilnehmerzahlen nach Zuwanderungsgeschichte',
      'Participation count per immigration history',
    ],
    icsrm_unterbuchungen_over_time: ['Workshop Ausbuchung (Prozent)', 'Workshop subscriptions (percentage)'],
    icsrm_unterbuchungen_over_time_with_immigration_history: [
      'Workshop Ausbuchungen mit Zuwanderungsgeschichte (Prozent)',
      'Workshop subscriptions with immigration history (percentage)',
    ],
    pie_chart_filter_label: ['Daten anzeigen für', 'Show data for'],
    pie_chart_registered: ['Registrierte Teilnahmen', 'Registered participations'],
    pie_chart_successful: ['Erfolgreiche Teilnahmen', 'Successful participations'],
  },

  bundle: {
    singular: ['Bundle', 'Bundle'],
    plural: ['Bundles', 'Bundles'],
    info: ['Verwalten von Bundels und Rabatten.', 'Manage bundels, discounts.'],
    desc: [
      'Bundles sind eine Bündelung einer vorausgewählten Terminliste. Diese kostet insgesamt weniger!',
      'Bundles are bundels of multiple appointments. It reduces the total cost!',
    ],
    confirm_deletion: ['Wollen Sie dieses Bundle wirklich löschen?', 'Do you really want to delete this Bundle?'],
    contains_following: [
      'Dieses Bundle umfasst folgende Workshops/Termine.',
      'This bundle contains the following workshops/appointments.',
    ],
    single_termin_manuel_selection: [
      'Einzelberatungen müssen manuell ausgewählt werden.',
      'Single-consultations have to be selected manually.',
    ],
    order_qualifies_for_coupon: [
      'Die Bestellung qualifiziert sich für diesen Rabatt!',
      'The Order qualifies for the coupon!',
    ],
    couldnt_select_bundle: [
      'Konnte Bundle nicht auswählen, da ein oder mehrere Veranstaltungen nicht ausgewählt werden können.',
      'The bundle could not be selected because one or more events cannot be selected.',
    ],
  },

  workshop: {
    import_error: ['Fehler beim Workshop-Import', 'Workshop import failed'],
    import_summary: ['Ergebnis des Workshop-Imports', 'Workshop import summary'],
    imported_count: ['Workshops importiert', 'workshops imported'],
    skipped_count: ['Zeilen übersprungen', 'rows skipped'],
    import_line: ['Zeile', 'Line'],
    singular: ['Workshop', 'Workshop'],
    plural: ['Workshops', 'Workshops'],
    finishedPlural: ['Abgeschlossene Workshops', 'Finished workshops'],
    currentPlural: ['Laufende Workshops', 'Current workshops'],
    upcoming_events: ['Zukünftige Veranstaltungen', 'Upcoming events'],
    past_events: ['Vergangene Veranstaltungen', 'Past events'],
    all_events: ['Alle Veranstaltungen', 'All events'],
    info: [
      'Verwalten von Workshops, Vorlagen und Semestern. Einsehen von Teilnehmerlisten.',
      'Manage workshops, templates and semesters. Viewing participant lists.',
    ],
    add: ['Workshop anlegen', 'add workshop'],
    edit: ['Workshop bearbeiten', 'edit workshop'],
    hide_capacity: ['Kapazität bei Registrierung ausblenden', 'Hide capacity on registration'],
    generate_registration_qrcode: ['QR Code zur Anmeldung generieren', 'generate QRCode for registration'],
    generate_participation_qrcode: ['QR Code zur Teilnahme generieren', 'generate QRCode for participation'],
    download_timeslots: ['Download Zeitslots', 'download timeslots'],
    registration_closed: ['', ''],
    export_err: [
      'Es gab ein Problem beim Exportieren der Workshops',
      'There was a error while exporting the workshops',
    ],
    confirm_cancelation: ['Wollen Sie die Veranstaltung wirklich absagen?', 'Do You really want to cancel this event?'],
    confirm_decancelation: [
      'Wollen Sie die Veranstaltung wirklich wieder aktivieren?',
      'Do You really want to re-activate this event?',
    ],
    send_certificates: ['Teilnahmezertifikate senden...', 'Send participation-certificates...'],
    send_certificate: ['Teilnahmezertifikat senden', 'Send participation-certificate'],
    generate_participation_signing_list: ['Teilnehmerzettel generieren...', 'Generate participation signing list...'],
    question_send_certificates: [
      'Sollen Zertifikate an alle erfolgreichen Teilnehmer dieses Workshops per Mail versandt werden?',
      'Do you really want to send the certificates to every participant?',
    ],
    successfully_sent_certificates: ['Zertifikate wurden erfolgreich versandt!', 'Sucessfully sent certificates!'],
    auto_nachruecken: [
      'wartende Teilnehmer automatisch nachrücken lassen, falls Plätze frei werden',
      'automatically register waiting participants in case seats become available',
    ],
    allowSubscribingToWaitinglistWithoutMeetingRequirements: [
      'Erlaube Teilnehmer sich, ohne die Teilnahmebedingungen zu erfüllen, in die Warteliste einzuschreiben',
      'Allow participants to subscribe to the waiting list without meeting entry-requirements',
    ],
    require_manual_admission: [
      'Manuelle Zulassung: alle Anmeldungen starten auf der Warteliste; Freischaltung nur durch Admin',
      'Manual admission only: all registrations start on the waitlist and must be approved by an admin',
    ],
    auto_send_certificates: [
      'Zertifikate werden nach Bestehen des Workshops automatisch versandt',
      'automatically send certificat-mails as soon as participants complete the workshop',
    ],
    workshopRecommendations: ['Weitere Workshops die Sie interessieren könnten', 'Workshops that could intresset you'],
    lecturerAlreadyAdded: [
      'Diese Person wurde bereits als Dozierender hinzugefügt',
      'This person has already been added as lecturer',
    ],
    addLecturer: ['Dozierende hinzufügen', 'Add lecturer'],
    lecturers: ['Dozierende', 'Lecturers'],
  },

  workshop_set: {
    singular: ['Workshop Reihe', 'Workshop Set'],
    plural: ['Workshop Reihen', 'Workshop Sets'],
    info: [
      'Verwalten von Workshop Reihen. Einsehen von Teilnehmerlisten.',
      'Manage workshop-Sets, templates and semesters. Viewing participant lists.',
    ],
    add: ['Workshop Reihe anlegen', 'add workshop set'],
    edit: ['Workshop Reihe bearbeiten', 'edit workshop set'],
  },

  ['workshop-notes']: {
    share: ['nur für Admins freigegeben', 'only accessible for admins'],
    unshare: ['für Dozenten freigegeben', 'accessible by lecturers'],
  },

  productEntryRequirement: {
    singular: ['Einschreibungsvoraussetzung', 'Entry requirement'],
    plural: ['Einschreibungsvoraussetzungen', 'Entry requirements'],
    ['ProductEntryRequirementForParticipation']: ['Workshop Teilnahme', 'Participation requirement'],
    ...icsrm.productEntryRequirements,
  },

  productWaitinglistPriorityRule: {
    ...icsrm.productWaitinglistPriorityRule,
  },

  counseling: {
    singular: ['Einzelberatung', 'Counseling'],
    plural: ['Einzelberatungen', 'Counselings'],
    info: [
      'Erstellen und Bearbeiten von Einzelberatungen. Verteilung der Beratung auf unterschiedliche Personen.',
      'Creating and editing individual consultations. Distribution of advice to different people.',
    ],
    add: ['Einzelberatung anlegen', 'Add counseling'],
    edit: ['Einzelberatung bearbeiten', 'Edit counseling'],
    possible_timeslots: ['Mögliche Zeitslots', 'possible timeslots'],
    err_startdate_required: ['Startdatum muss gesetzt sein', 'Start date has to be set'],
    err_enddate_required: ['Enddatum muss gesetzt sein', 'End date has to be set'],
    err_duration_required: ['Dauer muss gesetzt sein', 'Duration has to be set'],
    add_slots: ['Slot hinzufügen', 'add slots'],
    add_all: ['Alle hinzufügen', 'Add all'],
    no_availability_for_user: ['Für aktuellen Nutzer nicht verfügbar', 'no availability for current user'],
    participant: ['Verbindlich angemeldet', 'binding registration'],
    close_termin: ['Wollen Sie diesen Termin wirklich abschließen?', 'Do you really want to close this appointment'],
    participated_successfully: ['Erfolgreich teilgenommen', 'participated successfully'],
    mark_as_participated: ['Als Teilgenommen markieren', 'mark as participated'],
    close_timeslots: ['Zeitslot Ansicht schließen', 'close timeslot view'],
    advisor: ['Berater', 'Advisor'],
    add_advisor: ['neuen Berater hinzufügen', 'add new Advisor'],
    duration_per_slot: ['Dauer pro Slot', 'duration per slot'],
    delete_termin_confirmation: [
      'Wollen Sie diese Einzelberatung wirklich löschen? Es werden somit auch alle Teilnahmen unwiderruflich entfernt.',
      "Do you really want to delete the appointment? All Participants will be deleted too. This action can't be undone",
    ],
    slots: ['Zeiten', 'Slots'],
    lunchbreak: ['Mittagspause', 'Lunch-Break'],
    advisor_already_added: ['Der ausgewähte Nutzer ist bereits Berater', 'The selected User is already advisor'],
    berater_meeting_links: ['Meeting-Links pro Berater', 'Meeting links per advisor'],
  },

  timeframe: {
    singular: ['Zeitraum', 'Timeframe'],
    plural: ['Zeiträume', 'Timeframes'],
    add: ['Zeitraum hinzufügen', 'add timeframe'],
    info: [
      'Erstellen und Bearbeiten von Zeiträumen. Synchronisierung von Workshops und Einzelberatung von Zeitraum zu Zeitraum.',
      'Creating and editing time periods. Synchronization of workshops and individual consultations from period to period.',
    ],
    confirm_deletion: ['Wollen Sie diesen Zeitraum wirklich löschen?', 'Do you really want to delete this Timeframe'],
    sync_from: ['Synchronisieren aus anderem', 'sync from other'],
    deactivate_registration: ['Anmeldungen deaktivieren?', 'Deactivate registration'],
    deactivate_registration_wrn: [
      'Nutzer können sich dann für die Workshops nicht mehr registrieren.',
      'Users wont be able to register for workshops anymore',
    ],
    registration_open: ['Anmeldung offen', 'registrtaion open'],
    activate_registration: ['Anmeldungen aktivieren?', 'activate registration'],
    activate_registration_wrn: [
      'Nutzer können sich dann für die Workshops registrieren.',
      'Users will be able to register for workshops',
    ],
    registration_soon_available: ['Anmeldung bald verfügbar', 'Registration soon available'],
    over: ['vorbei', 'over'],
  },

  categories: {
    singular: ['Kategorie', 'Category'],
    plural: ['Kategorien', 'Categories'],
    info: [
      'Erstellen und löschen von Kategorien, für die Gruppierung von Workshops und Einzelberatungen.',
      'Creation and deletion of categories for grouping workshops and counselings.',
    ],
    set_name: [
      'Bitte geben Sie einen neuen Namen für diese Kategorie an.',
      'Please insert a new name for this category',
    ],
    err_refresh: [
      'Es gab ein Problem beim Aktualisieren der Kategorie. Bitte versuchen Sie es später erneut!',
      'There was an error while refreshing the category. Please try again later!',
    ],
    confirm_deletion: [
      'Wollen Sie diese Kategorie wirklich löschen? Es werden somit auch alle Teilnahmen unwiderruflich entfernt.',
      'Do you really want to delete this category. All participations will be deleted too',
    ],
    err_delete: [
      'Es gab ein Problem beim Entfernen der Kategorie. Möglicherweise sind der Kategorie noch Workshops zugeordnet, diese Verbindungen müssen zuerst aufgelöst werden.',
      'There was e Problem while deleting the category. Maybe there are still workshops using this category. To delete this category, you will have to cut this connection first',
    ],
    err_creation: [
      'Es gab ein Problem beim Erstellen der Kategorie. Bitte versuchen Sie es später erneut!',
      'There was an error while creating the category. Please try again later!',
    ],
    add: ['Kategory hinzufügen', 'add category'],
    edit: ['Kategory bearbeiten', 'edit category'],
  },

  user: {
    not_found: ['Nutzer wurde nicht gefunden', 'User not found'],
    details: ['Details', 'Details'],
    other: ['Sonstiges', 'Other'],
    write_mail: ['E-Mail schreiben', 'write e-mail'],
    share_user_with_institution: [
      `Wollen Sie die Suspendierung für diesen Nutzer wirklich aufheben?`,
      'Do you really want to anull the suspension of this user?',
    ],
    anulled_suspension: ['Suspendierung wird augehoben...', 'Anulled suspension...'],
    anull_suspension: ['Suspendierung aufheben', 'Anull suspension'],
    reg_to_event: ['Anmeldungen zu Veranstaltungen', 'Register to event'],
    suspended: ['suspendiert', 'suspended'],
    write: ['Anschreiben', 'write'],
    participations_del: [
      `Wollen Sie wirklich alle Teilnahmen des Nutzers löschen?`,
      'Do you really want to delete all participations of this user?',
    ],
    invoices: ['Rechnungen', 'Invoices'],
    claim_waitinglist_spot: ['nachrücken', 'claim spot'],
  },

  lecturer: {
    eventsHeld: ['Gehaltene Veranstaltungen', 'Events Held'],
    comments: ['Kommentare', 'Comments'],
    noDate: ['Kein Datum', ' No Date'],
    billingInformation: ['Rechnungsdaten in der Rolle als Dozent', 'Billing Information in the role of lecturer'],
    iban: ['IBAN', 'IBAN'],
    salutation: ['Anrede', 'Salutation'],
    title: ['Titel', 'Title'],
    phone: ['Telefon', 'Phone'],
    billingAddress: ['Rechnungsadresse', 'Billing Address'],
    bundesland: ['Bundesland', 'State'],
    country: ['Land', 'Country'],
    streetAndHousenumber: ['Straße und Hausnummer', 'Street and House Number'],
    zip: ['PLZ', 'Zip'],
    city: ['Stadt', 'City'],
    additionalInfo: ['Zusätzliche Informationen', 'Additional Information'],
    removeFromProduct: ['Dozent von diesem Produkt entfernen', 'Remove lecturer from product'],
    manageContractOnProduct: ['Vertrag verwalten', 'Manage contract'],
    edit: ['Dozent bearbeiten', 'edit lecturer'],
    files: ['Dateien', 'Files'],
  },

  management: {
    global_admin: ['Globaler Admin', 'global admin'],
    init_imitation: ['Immitierung wird initialisiert...', 'Initialize immmitation'],
    revoce_admin_privileges: [
      'Wollen Sie dieser Person wirklich die Admin-Berechtigungen entziehen?',
      'Do you really want to revoce admin-privileges for this person?',
    ],
    currently_immitating: ['Sie immitieren eine andere Person!', 'You are immitating someone else'],
    stop_immitating: ['Immitieren beenden', 'stop immitating'],
  },

  global_settings: {
    settings_saved: ['Globale Einstellungen erfolgreich gespeichert', 'Global settings saved'],
    error_saving: ['Es gab ein Problem beim Speichern der globalen Einstellungen', 'Error by saving the settings'],
    email_hint: [
      'E-Mail Vorlagen können hier für die ausgewählte Einrichtung überschrieben werden. Institutionsspezische E-Mail Vorlagen werden erst beim ersten Speichern erstellt. Die globalen E-Mail-Vorlagen werden beim Erstellen einer neuen Einrichtung kopiert!',
      'E-Mail templates can be overridden on an institution basis. Templates specific to the respective institution will only be created once the template has been initially saved. The global e-mail templates will be copied to all new institutions!',
    ],
    edit_email_templates: ['E-Mail-Templates bearbeiten', 'Edit email templates'],
  },

  admin_form_config: {
    changes_save_auto: ['Ihre Änderungen werden automatisch gespeichert.', 'Your changes will be saved automatically.'],
    no_fields: [
      'Bisher gibt es noch keine Felder auf den Stammdaten. Ziehe neue Felder aus den verfügbaren um bei der Anmeldung diese Informationen abzufragen.',
      'There are currently no fields in the master data. Drag new fields from the available ones to request this information when registering.',
    ],
    add_field: ['Neues Feld hinzufügen', 'Add new field'],
    can_used_by_institution: [
      'Dieses Feld kann nur von Ihrer Institution genutzt werden.',
      'This field can only be used by your institution.',
    ],
  },

  admin_institution: {
    maintenance_mode_active: ['Wartungsmodus aktiv', 'Maintenance mode active'],
  },
  NAME: ['Buchungsheld', 'Buchungsheld'],
  REQUEST_ADMIN_LINK: ['Verwaltungslink erhalten', 'Receive admin link'],
  MY_PROFILE: ['Mein Profil', 'My profile'],
  ADMINISTRATION: ['Administration', 'Administration'],
  LOGOUT: ['Abmelden', 'Logout'],
  LOGIN: ['Anmelden', 'Login'],
  REGISTER: ['Registrieren', 'Register'],
  WORKSHOP_OVERVIEW: ['Terminübersicht', 'Workshop Overview'],
  SIGNUP_TO_WORKSHOPS: ['Zu neuen Workshops anmelden', 'Signup for new workshops'],
  WORKSHOP_CAPACITY: ['Plätze frei', 'places free'],
  PAGE_NOT_FOUND: ['Da bist du irgendwo falsch abgebogen!', 'Page not found'],
  PROFILE: {
    TITLE: ['Profil', 'Profile'],
    ERROR_SAVING_PROFILE: [
      'Es gab ein Problem beim Speichern deines Profils',
      'There was a problem saving your profile',
    ],
    SUCCESS_SAVING_PROFILE: ['Profil erfolgreich gespeichert', 'Successfully saved profile'],
    SUCCESS_SAVING_LECTURER_DATA: [
      'Rechnungsdaten des Dozenten erfolgreich gespeichert',
      'Successfully saved billing information for lecturer',
    ],
    NAME_CHANGE_DISABLED: [
      'Ihr Name kann nicht mehr geändert werden, da Sie bereits an einer Veranstaltung teilgenommen haben. Bitte wenden Sie sich an den Veranstalter.',
      'Your name can no longer be changed because you have already participated in an event. Please contact the organizer.',
    ],
    EDIT_PROFILE: ['Profil bearbeiten', 'Edit profile'],
    CHANGE_PASSWORD: ['Password ändern', 'Change password'],
    NEW_PASSWORD: ['Neues Passwort', 'New password'],
    REPEAT_NEW_PASSWORD: ['Neues Passwort wiederholen', 'Repeat new password'],
    PASSWORDS_DONT_MATCH: ['Passwörter stimmen nicht überein', 'Password do not match'],
    PASSWORD_CHANGED_SUCCESSFULLY: ['Passwort erfolgreich geändert', 'Successfully changed password'],
    PASSWORD_CHANGED_ERROR: ['Passwort konnte nicht geändert werden', "Couldn't change password"],
    PASSWORD_CHANGED_ERROR_REQUIREMENTS: [
      'Passwort konnte nicht geändert werden. Beachten Sie die folgenden Vorraussetzungen: Das Passwort muss mindestens 8 Zeichen lang sein, einen Groß- und einen Kleinbuchstaben, sowie eine Zahl und ein Sonderzeichen beinhalten.',
      "Couldn't change password due to not meeting the minimum requirements: Password must contain atleast 8 characters, one uppercase letter, one lowercase, one number and one special character.",
    ],
    ZOOM_CONNECT: ['mit Zoom verbinden', 'Connect with Zoom'],
    ZOOM_DISCONNECT: ['Zoom verknüpfung trennen', 'Disconnect with Zoom'],
    CHANGE_EMAIL: ['E-Mail Adresse ändern', 'Change email address'],
    NEW_EMAIL: ['Neue E-Mail Adresse', 'New email address'],
    REPEAT_NEW_EMAIL: ['Neue E-Mail Adresse wiederholen', 'Repeat new email address'],
    EMAILS_DONT_MATCH: ['E-Mail Adressen stimmen nicht überein', 'Email addresses do not match'],
    EMAIL_CHANGED_SUCCESSFULLY: ['E-Mail Adresse erfolgreich geändert', 'Successfully changed email address'],
    EMAIL_CHANGE_REQUEST_SENT: [
      'Wir haben ihnen eine E-Mail mit einem Link zum Bestätigen der E-Mail-Adresse geschickt.',
      'We sent them an email with a link to confirm their new email address.',
    ],
    EMAIL_CHANGED_ERROR: ['E-Mail Adresse konnte nicht geändert werden', "Couldn't change email address"],
    SIGN_OFF: ['Abmelden', 'Sign off'],
    NO_WORKSHOPS: [
      'Zu keinen Workshops angemeldet. Melde dich jetzt an!',
      'Not signed up for any workshops. Sign up now!',
    ],
    SUSPENDED: ['Suspendiert', 'Suspended'],
    NEW_NAME: ['Neuer Name', 'new name'],
    pat_desc: ['Beschreibung', 'Description'],
    pat_status: ['Teilnahmestatus', 'Participation status'],
    pat_change_status: ['Status ändern', 'change status'],
    book: ['Anmelden', 'book'],
    cancle_booking: ['Abmelden', 'Cancel booking'],
    single_appointment_planned_for: [
      'Ihre Einzelberatung is geplant für den',
      'Your single-appointment is plannned for',
    ],
    GENERATE: ['Generieren', 'Generate'],
    CANCEL: ['Abbrechen', 'Cancel'],
    paid: ['Bezahlt', 'paid'],
    not_paid: ['nicht Bezahlt', 'not paid'],
    PARTICIPATED_EVENTS: ['Veranstaltungen', 'Events'],
    EVENTS_PLANNED: ['Zukünftige Events', 'Future Events'],
    EVENTS_COMPLETED: ['Vergangene Events', 'Past Events'],
    EVENTS_USER: ['Veranstaltungen', 'Events'],
    NO_PLANNED_EVENTS: ['Keine aktuellen Veranstaltungen', 'No current events'],
    NO_COMPLETED_EVENTS: ['Keine vergangenen Veranstaltungen', 'No past events'],
    no_events: ['Keine Veranstaltungen mehr ausstehend', 'currently their are no events'],
    next_termin: ['Ihr nächster Termin ist geplant für den', 'Your next appointment is plannned for'],
    no_workshops: [
      'Dieser Workshop besitzt noch keine Veranstaltungs-Termine.',
      'This workshop does not has any events yet',
    ],
    all_appointments: ['alle Termine', 'all Appointments'],
    all_documents: ['alle Dokumente', 'all Documents'],
    icsFile: ['Kalender-Datei', 'calendar file'],
    icsLinkNote: [
      'Alle Termine als Link in Ihren Kalender eintragen und alle Updates automatisch synchronisieren. Neben jedem einzelnen Termin lassen sich ICS-Dateien gesondert herunterladen.',
      'Link all your appointments to your calendar and automatically synchronize all changes. Next to each individual appointment, ICS files can be downloaded separately.',
    ],
    generate_global_certificate: ['Gesamtbescheinigung herunterladen', 'download global certificate'],
    certificate_language: ['Sprache', 'Language'],
    certificate_lang_de: ['Deutsch', 'German'],
    certificate_lang_en: ['Englisch', 'English'],
    certificate_template_missing: [
      'Für diese Sprache wurde keine Vorlage hinterlegt. Bitte wenden Sie sich an Ihre Einrichtung.',
      'No template has been uploaded for this language. Please contact your institution.',
    ],

    // Notifications
    NOTIFICATIONS: {
      header: ['Benachrichtigungs Einstellungen', 'Notifications Settings'],
      whatsapp: ['Whatsapp', 'Whatsapp'],
      whatsapp_declaration: [
        `Ich stimme zu, dass Buchungsheld mir Nachrichten via WhatsApp zusendet.
  Buchungsheld darf für diesen Vorgang meine Telefonnummer, Namen und Details zur Buchung an Meta Platforms Inc weitergeben.
  Ich kann diese Zustimmung jederzeit widerrufen.`,
        `I agree that Buchungsheld may send me messages via WhatsApp.
  Buchungsheld is permitted to share my phone number, name, and booking details with Meta Platforms Inc. for this purpose.
  I can withdraw this consent at any time.`,
      ],
      checkboxLabel: ['Ich stimme zu', 'I agree'],
      newsletter: ['Newsletter', 'Newsletter'],
      get_newsletter_mails: [
        'Ich möchte über neue Workshops und Angebote informiert werden',
        'I want to get notified about new workshops and offers',
      ],
      whichNumberHeader: ['und möchte Meldungen erhalten auf', 'and I want to get the messages for'],
      numberPlaceholder: ['WhatsApp-Nummer', 'WhatsApp number'],
    },

    // List of events for a lecturer
    EVENTS_TEACHING: ['Veranstaltungen (als Dozierender)', 'events (as lecturer)'],
    OVERVIEW_TEACHINGS: {
      TITLE: ['Übersicht über angebotene Veranstaltungen', 'Overview of my lecturing events'],
      back_to_overview: ['Zurück zur Veranstaltungsübersicht', 'Back to the events overview'],
    },
    CONTRACTS: ['Verträge', 'contracts'],
    OVERVIEW_CONTRACTS: {
      TITLE: ['Übersicht über die abgeschlossenen Verträge', 'Overview of my contracts'],
    },
    MATCHING_WEEK: ['Matching Week', 'Matching Week'],
  },
  WORKSHOPS: {
    TITLE: ['Workshops', 'Workshops'],
    meeting_link: ['Meeting-Link', 'Meeting link'],
    meeting_code: ['Meeting-Code', 'Meeting code'],
    meeting_password: ['Passwort', 'Password'],
    STATES: {
      [ParticipationState.participated]: ['Erfolgreich teilgenommen', 'participated'],
      [ParticipationState.in]: ['Verbindlich angemeldet', 'binding registration'],
      [ParticipationState.waiting]: ['In Warteliste', 'Waiting'],
      [ParticipationState.left]: ['Abgemeldet', 'Left'],
      [ParticipationState.suspended]: ['Suspendiert', 'Suspended'],
      [ParticipationState.no_show]: ['Nicht erschienen', 'No Show'],
    },
    registration_success: ['Erfolgreich zu Produkt angemeldet', 'Successfully registered for product'],
    cancel_success: ['Erfolgreich von Produkt abgemeldet', 'Successfully canceled product'],
    inform_participants_conf: [
      'Sollen Teilnehmer über die Änderungen per E-Mail informiert werden?',
      'Should participants get an email notification about the changes',
    ],
  },
  SAVE: ['Speichern', 'Save'],
  download: ['Download', 'Download'],

  form: {
    confirm_leave_page: ['Wollen Sie die Seite wirklich verlassen?', 'Do you really want to leave this page?'],
  },

  validation_non_optional: ['Dies ist ein Pflichtfeld', 'This field is required'],
  validation_digits_only: ['Bitte nur Ziffern eingeben', 'Please enter digits only'],
  validation_too_low: ['Der Wert ist zu klein', 'This value is to small'],
  validation_too_large: ['Der Wert ist zu groß', 'This value is to big'],
  validation_required_fields_missing: [
    'Bitte füllen Sie alle Pflichtfelder aus',
    'Please fill out all required fields',
  ],
  invalid_invoice_receiver: ['Ungültiger Rechnungsempfänger', 'Invalid invoice receiver'],
  check_form: ['Bitte überprüfen Sie ihre Eingaben', 'Please check your inputs'],
  firstReminderBeforeSecond: [
    'Erste Erinnerung muss nach der zweiten liegen',
    'First reminder must be after the second',
  ],
  secondReminderAfterFirst: [
    'Zweite Erinnerung muss nach der ersten liegen',
    'Second reminder must be after the first',
  ],
  max_price_err: ['Der gewählte Preis ist zu hoch.', 'Chosen price is to high.'],
  invalid_email: ['Die E-Mail ist ungültig', 'The email is invalid'],
  appointment_start_needs_to_be_smaller_than_end: [
    'Startzeit muss vor der Endzeit liegen',
    'Start time needs to be before end time',
  ],

  INSTITUTIONS: {
    newInstitution: ['Neue Einrichtung', 'New Institution'],
    addNewInstitution: ['Neue Einrichtung hinzufügen', 'Add new institution'],
    addingNewInstitution: ['Neue Einrichtung wird hinzugefügt...', 'Adding new institution...'],
    addedNewInstitution: ['Angelegt!', 'Added!'],
    customFieldsHeading: [
      'Benutzerdefinierte Felder der Stammdaten für diese Einrichtung',
      'Custom fields of core data for this institution',
    ],
    addableCustomFields: ['Stammdatenfelder zum Hinzufügen', 'Custom fields to add'],
    userInfo: ['Nutzer-Informationen für diese Einrichtung', 'User information for this institution'],
    sucessfullySavedMetaData: [
      'Stammdatenfelder erfolgreich gespeichert!',
      'Successfully saved available custom fields!',
    ],
    errorSavingMetaData: [
      'Es gab ein Problem beim Speichern der verfügbaren benutzerdefinierten Felder. Bitte versuchen Sie es später erneut oder wenden Sie sich an Ihren Administrator!',
      'There was a problem saving the available custom fields. Please try again later or contact your administrator',
    ],
  },

  NEWSLETTER: {
    exportNewsletterHeading: ['Newsletter-Adressen exportieren', 'Export newsletter addresses'],
    exportNewsletterDescription: [
      'Alle Nutzer dieser Einrichtung, die dem Erhalt eines Newsletters zugestimmt haben, exportieren.',
      'Export all users of this institution which agreed to receiving a newsletter.',
    ],
  },

  PAYMENT: {
    payment: ['Zahlung', 'Payment'],
    mark_as_paid: ['als bezahlt markieren', 'mark as paid'],
    update_status: [
      'Wollen Sie wirklich den Payment Status updaten?',
      'Do you really want to update the payment status?',
    ],
    STATE: {
      created: ['erstellt', 'created'],
      issued: ['ausgestellt', 'issued'],
      canceled: ['abgebrochen', 'canceled'],
      paid: ['bezahlt', 'paid'],
      refund: ['erstattet', 'refund'],
      expired: ['abgelaufen', 'expired'],
      failed: ['fehlgeschlagen', 'failed'],
    },
    select_method: ['Bitte wählen Sie eine Zahlungsmethode.', 'Please select payment method.'],
    method: ['Zahlungsmethode', 'Payment method'],
    methods: {
      direct: ['Online bezahlen', 'Pay online'],
      card: ['Mit Kreditkarte zahlen', 'Pay with Creditcard'],
      paypal: ['Mit PayPal zahlen', 'Pay with PayPal'],
      invoice: ['Mit Rechnung bezahlen', 'Pay with Invoice'],
      invoice_cost_center: ['Via Kostenstelle bezahlen', 'Pay via cost center'],
    },
  },

  REGISTRATION: {
    confirmed_email_cannot_change: [
      'Ihre E-Mail-Adresse ist bereits bestätigt und kann hier nicht geändert werden. Bitte ändern Sie sie in Ihrem Profil.',
      'Your e-mail address is already confirmed and cannot be changed here. Please change it in your profile.',
    ],
    overlapping_timeslots: [
      'Sie haben bereits Termine gebucht die sich mit der aktuellen Auswahl überschneiden. Bitte überprüfen sie ihre Eingaben',
      'You have already booked appointments that overlap with the current selection. Please check your entries.',
    ],
    step_failed: [
      'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
      'An error occurred. Please try again.',
    ],
    i_want_to_receive_newsletter: ['Ich möchte Newsletter erhalten', 'I want to receive newsletter'],
    i_want_to_receive_whatsapp_notifications: [
      'Ich möchte Whatsapp-Nachrichten erhalten',
      'I want to receive whatsapp notifications',
    ],
    phone_number_edit: ['Telefonnummer bearbeiten', 'Edit phone number'],
    category_is_empty: [
      'In der ausgewählten Kategorie finden dieses Semester keine Veranstaltungen mehr statt.',
      'There are no further events offered this semester in the chosen category.',
    ],
    no_results: ['Derzeit gibt es keine verfügbaren Veranstaltungen', 'No available events'],
    noOTPEntered: ['Login Code nicht eingegeben!', "Didn't enter login token!"],
    problemOccuredResendingOTP: [
      'Es gab ein Problem beim erneuten Zusenden des Login Codes. Bitte kontaktieren Sie uns.',
      'There was a problem resending your Login Token. Please contact us to resolve the issue',
    ],
    successfullyResendConfirmationCode: ['Login Code wurde erneut versandt', 'Resent Login Code'],
    cannotSelectAppointmentCollision: [
      'Sie haben bereits einen Workshop zum selben Zeitpunkt',
      'You already have selected a Workshop at the same time',
    ],
    cannotSelectAppointmentCollisionTooltip: [
      'Dieser Termin kann aufgrund deiner aktuellen Terminauswahl wegen terminlicher Überschneidungen nicht ausgewählt werden. Deselektiere den überschneidenden Termin um diesen auswählen zu können.',
      'Cannot select this appointment because of a time collision with another selection. Deselect the other appointment in order to select this one.',
    ],
    appointmentAlreadyBooked: ['Termin bereits gebucht', 'Appointment already booked'],
    alreadyRegistered: ['Sie sind bereits angemeldet', 'You are already registered'],
    alreadyOnWaitingList: ['Sie stehen bereits auf der Warteliste', 'You are already on the waiting list'],
    appointmentAlreadyBookedTooltip: [
      'Dieser Termin ist auf dem aktuellen Account bereits gebucht und kann nicht doppelt belegt werden.',
      'This appointment is already booked on the current account and cannot be booked twice.',
    ],
    errorCollidingAppointmentTimes: [
      'Sie wurden erfolgreich zu den ausgewählten Veranstaltungen angemeldet. Allerdings gab es eine Terminüberschneidung mit einem bereits angemeldeten Produkt. Vergewissern Sie sich, dass sich die Veranstaltungszeiträume nicht überschneiden.',
      'You already registered to other Workshops. Please ensure the times of your registrations do not collide.',
    ],
    errorCollidingAppointmentTimesExplanation: [
      'Folgende Produkte konnten wegen Zeitüberschneidung nicht gebucht werden',
      "The following products couldn't be booked because of time overlaps with already booked products",
    ],
    switch_account: ['Account wechseln', 'switch account'],
    mail_confirmation_required: ['Sie müssen zuerst Ihre E-Mail bestätigen!', 'You first have to confirm you e-mail!'],
    err_mailing: [
      'Es gab ein Problem beim Versenden der Mail, bitte versuchen Sie es später erneut!',
      'There was an error while sending the mail. Please try again later!',
    ],
    auth: ['Login', 'Login'],
    send_again: ['E-Mail erneut verschicken.', 'send e-mail again.'],
    overview_send: [
      'Wir haben Ihnen nochmals eine Veranstaltungsübersicht per Mail geschickt. In der Mail finden Sie auch einen Link, um sich für weitere Veranstaltungen anzumelden bzw. abzumelden',
      'We send you the event overview per mail. In that mail is also a link you can use to register for other events or cancel registrations.',
    ],
    events_booked: ['Gebuchte Veranstaltungen', 'Booked events'],
    personal_data: ['Persönliche Daten', 'Personal information'],
    enter_code: ['Code eingeben', 'enter code'],
    enter_code_expl: [
      'Wir haben Ihnen einen 6-stelligen Code geschickt. Geben Sie diesen hier ein.',
      'We sent you a 6-digit code. Please enter that code.',
    ],
    preparing_payment: ['Bezahlungsvorgang wird vorbereitet...', 'pereparing payment process...'],
    back_to_login: ['Zurück zur Anmeldung', 'back to login'],
    you_got_suspended: [
      'Sie wurden leider von dieser Einrichtung suspendiert und können somit Ihre Anmeldung nicht durchführen.',
      'Unfortunetly you got suspended at this institution and can therefore not login',
    ],
    could_not_auto_login: [
      'Konnte nicht automatisch eingeloggt werden. In der Bestätigungsmail gibt es einen Login-Link.',
      'Auto login was not possible. Please use the login-link in your confirmation e-mail.',
    ],
    wrong_code: ['Falscher Code eingegeben. Versuchen Sie es erneut.', 'The code is invalid. Please try again.'],
    read_terms_of_use: [
      'Ich habe die Nutzungsbedingungen und Datenschutzvereinbarung gelesen und akzeptiere sie.',
      'I have read the terms of use and privacy policy and accept them.',
    ],
    require_terms_of_use: ['Sie müssen den Nutzungsbedingungen zustimmen.', 'You have to accept the terms of use'],
    err_saving: [
      'Es gab ein Problem beim Speichern der Registrierungsdaten',
      'There was an error while saving the registration information',
    ],
    book_now: ['Jetzt buchen', 'book now'],
    queue_possible: ['Wartelistenplatz möglich', 'queue possible'],
    seat_not_available: ['Platz vergeben', 'seat is no longer available'],
    to_registration: ['Zur Registrierung', 'Registration'],
    selected_appointments: ['Ausgewählte Termine', 'Selected Appointments'],
    invalid_appointment_selected: ['Ungültiger Termin ausgewählt', 'Invalid appointment selected'],
    chosen_bundles: ['Ausgewählte Rabatte', 'chosen bundles'],
    change_selection: ['Auswahl ändern', 'change selection'],
    choose_termin: ['Termine auswählen', 'Appointment'],
    complete_registration: ['Anmeldung abschließen', 'Complete registration'],
    to_profile: ['Zum Profil', 'To your profile'],
    stammdaten: ['Institutionsspezifische Angaben', 'Institution speicific information'],
    input_too_long: [
      'Ihr Eingabewert übersteigt die maximal erlaubte Zeichenanzahl',
      'Your input exceeds the maximum allowed character limit.',
    ],
    includes_compulsory_appointment: ['enthält Termin ohne Anwesenheitspflicht', 'includes compulsory appointment'],
    non_compulsory: ['keine Anwesenheitspflicht', 'non compulsory'],
    show_detail_page: ['Details anzeigen', 'show detail page'],
  },

  FILTER: {
    timeframeIDs: ['Semester', 'Semester'],
    categoryIDs: ['Kategorie', 'Category'],
    topics: ['Thema', 'Topic'],
    presences: ['Veranstaltungsformat', 'Presence'],
    languages: ['Sprache', 'Language'],
    hasFreeSeats: ['Freie Plätze', 'Free Seats'],
  },

  SIDEBAR: {
    sd_invoices: ['SD-Rechnungen', 'SD invoices'],
    dashboard: ['Dashboard', 'Dashboard'],
    workshops: ['Workshops', 'Workshops'],
    workshop_sets: ['Workshop Reihen', 'Workshop Sets'],
    bundles: ['Bundle', 'Bundle'],
    statistics: ['Statistiken', 'Statistics'],
    participants: ['Teilnehmer', 'Participants'],
    imExport: ['Export', 'Export'],
    settings: ['Einstellungen', 'Settings'],
    admins: ['Administrator:innen', 'Administrators'],
    lecturers: ['Dozierende', 'Lecturers'],
    translations: ['Übersetzungen', 'Translations'],
    logout: ['Abmelden', 'logout'],
    counselings: ['Einzelberatungen', 'Counselings'],
    timeframes: ['Semester', 'Semesters'],
    categories: ['Kategorien', 'Categories'],
    home: ['Zur Startseite', 'To homepage'],
    chooseInstitution: ['Bitte auswählen', 'Please choose'],
    chooseToSeeMore: [
      'Wählen Sie eine Einrichtung um mehr Optionen zu sehen.',
      'Choose an institution to see more options.',
    ],
    users: ['Teilnehmer', 'Participants'],
    'form-config': ['Formularfelder', 'Form Fields'],
    mailing_inspector: ['Mail Logs', 'Mail Logs'],
  },

  LOGIN_PAGE: {
    sendLoginLink: ['Anmeldelink erhalten', 'Get Login-Link'],
    loginWithPassword: ['Anmeldung mit Passwort', 'Login with password'],
    loginWithoutPassword: ['Ohne Passwort anmelden', 'Login without password'],
    loginWithMail: ['Mit E-Mail anmelden', 'Login with e-mail'],
    loginWithSaml: ['Mit Uni-Account anmelden (Shibboleth)', 'Login with Uni-Account (Shibboleth)'],
    backToLogin: ['Zurück zur Anmeldung', 'Back to Login'],
    createAccount: ['Konto erstellen', 'Create account'],
    createdAccount: ['Benutzer wurde erfolgreich angelegt.', 'Successfully created account.'],
    forgotPassword: ['Passwort vergessen', 'Forgot password'],
    createNewAccount: ['Noch kein Konto? Neues Konto erstellen', 'No account? Create a new one'],
    emailOrPasswordWrong: ['E-Mail oder Passwort falsch', 'Wrong password or email address'],
    invalidInput: ['Ungültige Eingaben', 'Invalid input'],
    successfullySentPasswordResetLink: ['Anmeldelink wurde versandt', 'Sent password reset link'],
    back: ['Zurück', 'Back'],
    next: ['Weiter', 'Next'],
    enterValidMailAddress: ['Gib eine gültige E-Mail-Adresse ein.', 'Enter a valid E-Mail address.'],
    loginInNewTab: ['Login in neuem Tab abschließen.', 'Login continues in new tab'],
    userDoesNotExist: [
      'Es wurde kein Nutzerkonte für diese E-Mail Adresse gefunden.',
      'No user account was found for this mail address.',
    ],
  },

  COLOR_EDITOR: {
    REGISTRATION_FORM: ['Anmeldeformular', 'Registration form'],
    MAILS: ['E-Mails', 'E-mails'],
    stepColor: ['Step Hintergrund', 'step Background'],
    stepFontColor: ['Step Textfarbe', 'step text color'],
    productColor: ['Zeitangabe Hintergrund', 'time indicator Background'],
    productFontColor: ['Zeitangabe Textfarbe', 'time indicator text color'],
    moreProductsColor: ['Alle Termine Hintergrund', 'all appointments Background'],
    moreProductsFontColor: ['Alle Termine Textfarbe', 'all appointments text color'],
    chipInfosColor: ['Zusätzliche Infos Hintergrund', 'additional infos background'],
    chipInfosFontColor: ['Zusätzliche Infos Textfarbe', 'additional infos text color'],
    oldPriceColor: ['Alter Preis', 'old price'],
    newPriceColor: ['Aktueller/Neuer Preis', 'current/new price'],
    layoutLightContrastColor: ['Hintergrund schwacher Kontrast', 'Layout light contrast'],
    warnColor: ['Warnfarbe', 'warning color'],
    notDefined: ['Nicht definiert (Standard wird genutzt)', 'not defined (uses default)'],
    accentColor: ['Akzent Farbe', 'Accent Color'],
    accentContrastFontColor: ['Akzent Kontrast Textfarbe', 'Accent contrast text color'],
    PROFILE_PAGE: ['Nutzer-Profil', 'User profile'],
    LOGIN_PAGE: ['Login-Seite', 'Login page'],
    navigationActiveColor: ['Aktive Navigation', 'active navigation'],
    tabsColor: ['Aktive Tabs', 'active tabs'],
    buttonColor: ['Button Hintergrund', 'button background'],
    buttonFontColor: ['Button Textfarbe', 'button text color'],
    outlineButtonColor: ['Outline-Button Farbe (Rahmen & Text)', 'outline button color (border & text)'],
    calendarSyncColor: ['Kalender-Synchronisation Hintergrund', 'calendar sync background'],
    calendarSyncFontColor: ['Kalender-Synchronisation Textfarbe', 'calendar sync text color'],
    calendarSyncFieldColor: ['Kalender-Synchronisation Feld Hintergrund', 'calendar sync field background'],
    calendarSyncFieldFontColor: ['Kalender-Synchronisation Feld Textfarbe', 'calendar sync field text color'],
  },

  MAIL_EDITOR: {
    copy_from_template: ['Vorlage von Globalen Einstellungen kopieren', 'Copy template out of global settings'],
  },

  APPOINTMENT: {
    dateIsYetToBeDetermined: ['Datum steht noch nicht fest', 'Datum is yet to be determined'],
    ['MeetingLink']: ['Meeting Link', 'Meeting Link'],
    ['ZoomMeetingLink']: ['Zoom Meeting Link', 'Zoom Meeting Link'],
  },

  PROMPT: {
    user_management: [
      'Hier können Sie neue Nutzer erstellen oder existierende Nutzer zu Administratoren umwandeln:',
      'Here you can create users and also promote users to admins',
    ],
    edit_admins: [
      'Hier können Sie bereits existierende Administratoren editieren:',
      'Here you can edit already existing admins',
    ],
    edit_user_rights: ['Rechte von ausgewählten Nutzer editieren', 'Edit rights of selected users'],
    add_appointment: ['Termin hinzufügen', 'add Appointment'],
    edit_appointment: ['Termin bearbeiten', 'edit Appointment'],
    file_uploaded: ['Datei hochgeladen', 'uploaded File'],
    upload_crt_tmp: ['Zertifikat-Template hochladen (.docx)', 'upload Certificate-template (.docx)'],
    add_certificate: ['Zertifikat anlegen', 'add certificate'],
    edit_certificate: ['Zertifikat bearbeiten', 'edit certificate'],
    upload_document_tmp: ['Dokument hochladen', 'upload document'],
    add_workshop_document: ['Workshop Dokument anlegen', 'add workshop document'],
    edit_workshop_document: ['Workshop Dokument bearbeiten', 'edit workshop document'],
    saving_workshop_document: ['Workshop Dokument wird gespeichert...', 'Saving workshop document...'],
    add_workshop_note: ['Workshop Notiz anlegen', 'add workshop note'],
    edit_workshop_note: ['Workshop Notiz bearbeiten', 'edit workshop note'],
    saving_workshop_note: ['Workshop Notiz wird gespeichert...', 'Saving workshop note...'],
    add_semester: ['Semester anlegen', 'add semester'],
    edit_semester: ['Semester bearbeiten', 'edit semester'],
    user_or_data_not_found: ['Stammdaten oder Nutzer nicht gefunden!', 'No user or custom fields found!'],
    user_not_found_then_create: [
      'Sie können den Nutzer nicht finden? Legen Sie ihn hier an.',
      "You can't find the user you are looking for? Create it now.",
    ],
    user_no: ['Sie wollen doch keinen Nutzer anlegen?', 'You do not want to create the user anymore?'],
    user_select: ['Nutzer auswählen', 'select user'],
    user_add: ['Nutzer anlegen', 'add user'],
    suspension_of: ['Suspendierung von', 'Suspension of'],
    suspension_duration: ['Laufzeit der Suspendierung', 'Suspension period'],
    suspension_permanent: [
      'Für unbestimmte Zeit aus der Institution ausschließen',
      'Permanently suspend from institution',
    ],
    complete_sync: ['Sync abschließen', 'Complete sync'],
    confirm_sync: [
      'Wollen Sie wirklich folgende Workshops in dem ausgewählten Zeitraum synchronisieren?',
      'Do you really want to sync the workshops for the selected timeframe?',
    ],
    edit_selection: ['Auswahl bearbeiten', 'edit selection'],
    exec_sync: ['Sync durchführen', 'Execute Sync'],
    choose_timeframe: ['Zeitraum auswählen, welcher gesynct werden soll', 'Select timeframe which should be synced'],
    no_workshops_for_timeframe: [
      'Diesem Zeitraum sind keine Workshop zugeordnet.',
      'There are no woekshops in this timeframe',
    ],
    select_all: ['Alle auswählen', 'Select all'],
    select_none: ['Alle abwählen', 'Unselect all'],
    link_existing_person: ['Bestehende Person verlinken', 'interlink existing users'],
    create_person: ['Neue Person anlegen', 'Add new person'],
    create_lecturer: ['Neue Dozierende anlegen', 'Add new lecturer'],
    err_loading_user: [
      'Fehler beim Nutzer Abrufen. Bitte versuchen Sie es erneut.',
      'An error occured while loading the user. Please try again later.',
    ],
    qr_valid_from: ['gültig ab', 'valid from'],
    qr_valid_till: ['gültig bis', 'valid till'],
    qr_no_longer_valid: ['Nicht mehr gültig!', 'Not valid anymore!'],
    valid: ['Gültig', 'valid:'],
    add_institution_management: ['Einrichtungsverwaltung hinzufügen', 'Add institution management'],
    user: ['Benutzer', 'User'],
    user_couldnt_be_created_error: [
      `Nutzer konnte nicht erstellt werden. Bitte versuchen Sie es später erneut oder wenden Sie sich an Ihren Administrator${
        baseConfig.get('DEPLOYMENT') !== 'production'
          ? `. Da dies eine Entwicklungs-/Test-Instanz ist kann es sein, dass der Nutzer durchaus erstellt wurde, wegen der E-Mail Blacklist allerdings keine Mail versendet werden konnte`
          : ''
      }`,
      "Couldn't create user, please try again later",
    ],
    add_contract: ['Vertrag hinzufügen', 'Add contract'],
    upload_contract_tmp: ['Vertragsdatei hochladen', 'Upload contract file'],
  },

  // LECTURER
  LECTURERS: {
    placeholder: ['Bestehende Dozierende suchen...', 'Search exsting lecturers'],
  },

  CERTIFICATE: {
    validating_certificate: ['Zertifikat wird verarbeitet...', 'Certificate is getting processed...'],
    saving_certificate: ['Zertifikat wird gespeichert...', 'Saving certificate...'],
  },

  PRODUCT: {
    presenceType: ['Veranstaltungsformat', 'Presence Type'],
    presenceTypes: {
      not_selected: ['-----', '-----'],
      in_person: ['in Person', 'in person'],
      online: ['Online', 'online'],
      hybrid: ['Hybrid', 'hybrid'],
    },
    anbieter: ['Anbieter', 'Provider'],
    anbieterOptions: {
      frankfurt_uas: ['Frankfurt UAS', 'Frankfurt UAS'],
      goethe: ['Goethe-Universität', 'Goethe University'],
      hs_darmstadt: ['Hochschule Darmstadt', 'Hochschule Darmstadt'],
      hs_rheinmain: ['Hochschule RheinMain', 'Hochschule RheinMain'],
      jgu_mainz: ['JGU Mainz', 'JGU Mainz'],
      tu_darmstadt: ['TU Darmstadt', 'TU Darmstadt'],
      agentur_fuer_arbeit: ['Agentur für Arbeit', 'Agentur für Arbeit'],
      sonstige: ['Sonstige', 'Other'],
    },
    runtimeStatus: ['Laufzeitstatus', 'Runtime Status'],
    language: ['Kurssprache', 'Course language'],
    languages: {
      de: ['Deutsch', 'German'],
      en: ['Englisch', 'English'],
    },
    canceled: ['Event abgesagt', 'Event canceled'],
    editMode: {
      data: ['Daten', 'Data'],
      design: ['Design', 'Design'],
      participants: ['Teilnehmer', 'Participants'],
    },
    designEdit: {
      listing: ['Listenansicht', 'Listing view'],
      'workshop-card': ['Workshop Karte', 'Workshop card'],
      'counseling-card': ['Einzelberatung Karte', 'Counseling card'],
      'workshop-set-card': ['Workshop Reihe Karte', 'WorkshopSet card'],
    },
  },

  product: {
    addEntryRequirement: ['Einschreibungsvoraussetzung hinzufügen', 'Add entry requirements'],
    addPriorityRule: ['Prioritätsregel hinzufügen', 'Add priority rule'],
    manage_entry_requirement: ['Einschreibungsvoraussetzungen verwalten', 'Manage entry requirements'],
  },

  // DATA VIEW
  field: {
    // for dataview export fields
    ...ikh.field,
    ...sd.field,
    ...tuda.field,
    ...icsrm.field,
    ...icsrmFollowUp.field,
    ...deutschkurs.fieldShort,
    ...csjensblank.field,
    ...schluesselkompetenzen.field,
    ...demo.field,
    ...sdel.field,
    ...cpg.field,
    ...pbs.field,
    ...fjn.field,
    ...ttt.field,
    ...mzs.field,
    ...st.field,
    ...digitell.field,
    ...buildYourOwnAi.field,

    '': ['', ''],
    title: ['Titel', 'Title'],
    'template.category.name': ['Kategorie', 'Category'],
    'template.title': ['Vorlage', 'Template'],
    waitlist: ['Warteliste', 'Waiting List'],
    date: ['Datum', 'Date'],
    publishDate: ['Veröffentlichungs Datum', 'Publish Date'],
    appointments: ['Termine', 'Appointments'],
    detailPage: ['Detailseite', 'Detail page'],
    email: ['E-Mail', 'Email'],
    password: ['Passwort', 'Password'],
    username: ['Nutzername', 'Username'],
    participations: ['Teilnahmen', 'Participations'],
    workshops: ['Workshops', 'Workshops'],
    createdAt: ['Erstellt am', 'Created at'],
    sentAt: ['Versendet am', 'Sent at'],
    firstName: ['Vorname', 'First Name'],
    lastName: ['Nachname', 'Last Name'],
    vorname: ['Vorname', 'First Name'],
    nachname: ['Nachname', 'Last Name'],
    newsletter: ['Newsletter abonnieren', 'Subscribe newsletter'],
    matrikelNummer: ['Matrikelnummer', 'matriculation number'],
    fachschaft: ['Fachschaft', 'Fachschaft'],
    institutions: ['Institutionen', 'Institutions'],
    workshop: ['Workshop', 'Workshop'],
    state: ['Status', 'Status'],
    suspended: ['Suspendiert', 'Suspended'],
    infoLink: ['Link mit weiterführenden Informationen', 'Link for additional information'],
    description: ['Beschreibung', 'Description'],
    _class: ['Klasse (intern)', 'Class (internal)'],
    _id: ['ID (intern)', 'ID (internal)'],
    capacity: ['Kapazität', 'Capacity'],
    credits: ['AE / Credits', 'AE / Credits'],
    runtimeStatus: ['Laufzeitstatus', 'Runtime Status'],
    ingeritedCapacity: ['Kapazität', 'Capacity'],
    templateID: ['Workshop-Vorlagen-ID', 'Template ID'],
    template: ['Vorlage', 'Template'],
    semesterID: ['Semester-ID', 'Semester ID'],
    semester: ['Semester', 'Semester'],
    category: ['Kategorie', 'Category'],
    location: ['Örtlichkeit', 'Location'],
    reference: ['Referenz', 'Reference'],
    productCopyID: ['Termin Kopie ID', 'Product Copy ID'],
    workshopSetName: ['Workshop-Reihe', 'Workshop set'],
    age: ['Alter', 'Age'],
    alter: ['Alter', 'Age'],
    phone: ['Telefonnummer', 'Phone Number'],
    telefonnummer: ['Telefonnummer', 'Phone Number'],
    gotCertificate: ['Zertifikat erhalten', 'Certificate received'],
    buyer: ['Käufer', 'buyer'],
    address: {
      singular: ['Adresse', 'Address'],
      country: ['Land', 'Country'],
      bundesland: ['Bundestland', 'State'],
      additionalInfo: ['weitere Daten', 'additional info'],
      streetAndHousenumber: ['Straße und Hausummer', 'street and housenumber'],
      name: ['Adresse Person', 'Address Person'],
      zipCity: {
        zip: ['PLZ', 'Zip'],
        city: ['Stadt', 'City'],
      },
      billingAddress: ['Rechnungsadresse', 'Billing Address'],
    },
    street: ['Straße', 'Street'],
    houseNumber: ['Hausnummer', 'Number'],
    zip: ['PLZ', 'Zip'],
    city: ['Stadt', 'City'],
    country: ['Land', 'Country'],
    hasPrice: ['Kostenpflichtig', 'Charged'],
    unitAmount: ['Preis', 'Price'],
    tax_behavior: ['Steuer', 'Tax'],
    isNonBlocking: ['Blockiert keine anderen Workshops', 'Does not nlock other workshops'],
    isBooked: ['ist gebucht', 'is booked'],
    berater: ['Berater', 'Advisor'],
    certificate: ['Zertifikat', 'Certificate'],
    birthday: ['Geburtstag', 'Birthday'],
    geburtstag: ['Geburtstag', 'Birthday'],
    updatedAt: ['Letzte Änderung am', 'Last Change'],
    participated: ['Teilgenommen', 'Participated'],
    waitingListPlace: ['Wartelistenplatz', 'Waiting List Number'],
    registrationDate: ['Registrierung', 'registration'],
    signoffDate: ['Abmeldung', 'singoff'],
    registrationDateThroughAdmin: ['Anmeldung (durch Veranstalter)', 'registration (through Admin)'],
    signoffDateThroughAdmin: ['Abmeldung (durch Veranstalter)', 'signoff (through admin)'],
    categoryID: ['Kategorie', 'Category'],
    sellerID: ['Verkäufer', 'Seller'],
    institutionID: ['Einrichtung', 'Institution'],
    timeframeID: ['Semester', 'Semester'],
    certificateID: ['Zertifikat', 'Certificate'],
    isCanceled: ['Abgebrochen', 'Canceled'],
    productPriceForUser: ['Preis für Nutzer', 'Price for user'],
    receivesNewsletter: ['erhält Newsletter', 'receives newsletter'],
    nachrueckPossible: ['nachrücken möglich', 'moving up possible'],
    nachrueckPriority: ['nachrück Priorität', 'moving up priority'],
    lastStateChangeWasInitiatedByAdmin: [
      'letzte Statusänderung wurde durch einen Admin herbeigeführt',
      'last state change was inititated by admin',
    ],
    firstReminderLeadTime: ['Erste Erinnerung x Tage vorher', 'First Reminder x Days ahead'],
    secondReminderLeadTime: ['Zweite Erinnerung x Tage vorher', 'Second Reminder x Days ahead'],
    inheritedColorDefinitions: ['Farben (abgeleitet)', 'Colors (inherited)'],
    blockSignOffDaysInAdvance: ['Abmeldefrist', 'Signoff Block Range'],
    inheritedBlockSignOffDaysInAdvance: ['Abmeldefrist (abgeleitet)', 'Signoff Block Range (inherited)'],
    blockAutomaticMoveUpDaysInAdvance: ['Auto Wartenlisten Update Frist', 'auto waiting list update period'],
    inheritedBlockAutomaticMoveUpDaysInAdvance: [
      'Auto Wartenlisten Update Frist (abgeleitet)',
      'auto waiting list update period (inherited)',
    ],
    blockActiveRegistrationHoursInAdvance: ['Aktive Anmeldefrist', 'Active Registration Deadline'],
    inheritedBlockActiveRegistrationHoursInAdvance: [
      'Aktive Anmeldefrist (abgeleitet)',
      'Active Registration Deadline (inherited)',
    ],
    interAccountingEmail1: ['Interne Accounting E-Mail 1', 'internal accounting e-mail 1'],
    inheritedInterAccountingEmail1: [
      'Interne Accounting E-Mail 1 (abgeleitet)',
      'internal accounting e-mail 1 (inherited)',
    ],
    interAccountingEmail2: ['Interne Accounting E-Mail 2', 'internal accounting e-mail 2'],
    inheritedInterAccountingEmail2: [
      'Interne Accounting E-Mail 2 (abgeleitet)',
      'internal accounting e-mail 2 (inherited)',
    ],
    interAccountingEmail3: ['Interne Accounting E-Mail 3', 'internal accounting e-mail 3'],
    inheritedInterAccountingEmail3: [
      'Interne Accounting E-Mail 3 (abgeleitet)',
      'internal accounting e-mail 3 (inherited)',
    ],
    sdContactEmail: ['SD-Kontakt E-Mail', 'SD contact e-mail'],
    inheritedSdContactEmail: ['SD-Kontakt E-Mail (abgeleitet)', 'SD contact e-mail (inherited)'],
    sdCentralAccountingEmail: ['Zentrale Buchhaltung E-Mail', 'central accounting e-mail'],
    inheritedSdCentralAccountingEmail: [
      'Zentrale Buchhaltung E-Mail (abgeleitet)',
      'central accounting e-mail (inherited)',
    ],
    courseLocation: ['Kurs Ort', 'course Location'],
    inheritedCourseLocation: ['Kurs Ort (abgeleitet)', 'course Location (inherited)'],
    sendInvoiceDaysInAdvance: [
      'Rechnungen werden X Tage vor Beginn gesendet',
      'invoices are going to be sent x days in advance',
    ],
    inheritedSendInvoiceDaysInAdvance: [
      'Rechnungen werden X Tage vor Beginn gesendet (abgeleitet)',
      'invoices are going to be sent x days in advance (inherited)',
    ],
    paymentMethods: ['Zahlungsmethoden', 'payment methods'],
    inheritedPaymentMethods: ['Zahlungsmethoden (abgeleitet)', 'payment methods (inherited)'],
    replyToMailAddress: ['E-Mail Antwort-Adresse', 'email reply address'],
    hasLunchBreak: ['Mittagspause vorgesehen', 'lunchbreak exists'],
    startLunch: ['Mittagspause Start', 'Lunchbreak start'],
    endLunch: ['Mittagspause Ende', 'Lunchbreak end'],
    leftCapacity: ['verbleibende freie Plätze', 'remaining free seets'],
    name: ['Name', 'Name'],
    startDate: ['Startdatum', 'Start Date'],
    endDate: ['Enddatum', 'End Date'],
    slotDuration: ['Dauer einer Einheit', 'Slot Duration'],
    studienfach: ['(Studien)fach', 'Subject'],
    plz: ['PLZ', 'Zip Code'],
    user: ['Nutzer', 'User'],
    product: ['Produkt', 'Product'],
    prices: ['Preise', 'Prices'],
    active: ['Aktiv', 'Active'],
    inactive: ['Inaktiv', 'Inactive'],
    currency: ['Währung', 'Currency'],
    descriptionHTML: ['Beschreibung (HTML)', 'Description (HTML)'],
    sortingWeight: ['Sortierung', 'Sorting'],
    timeframe: ['Semester', 'Semester'],
    from: ['Von', 'From'],
    till: ['Bis', 'Until'],
    isActive: ['Aktiv', 'Active'],
    bildungsgrad: ['Bildungsgrad', 'Educational Degree'],
    isRegistrationPossibleBesidesTimeframe: [
      'Registrierung außerhalb des Semester möglich',
      'Registration outside semester possible',
    ],
    emailAlreadyInUse: [
      'Diese E-Mail wird bereits verwendet. Bitte melden Sie sich an, oder verwenden Sie eine andere E-Mail Adresse.',
      'This e-mail address is already in use. Please login or use an alterernative address.',
    ],
    emailConfirmed: ['E-Mail bestätigt', 'E-Mail confirmed'],
    suspentions: ['Suspendierungen', 'Suspensions'],
    message: ['Nachricht', 'Message'],
    blockedNoLimit: ['Blockiert ohne automatische Aufhebung', 'Blocked with no automatic cancelation'],
    blockedTill: ['Blockert bis', 'Blocked until'],
    participation: ['Teilnahme', 'Participation'],
    stammdaten: ['Institutionsspezifische Daten', 'Institution specific user data'],
    inheritedFirstReminderLeadTime: [
      'Erste Erinnerung x Tage vorher (abgeleitet)',
      'First Reminder x Days ahead (inherited)',
    ],
    inheritedSecondReminderLeadTime: [
      'Zweite Erinnerung x Tage vorher (abgeleitet)',
      'Second Reminder x Days ahead (inherited)',
    ],
    start: ['Start', 'Start'],
    end: ['Ende', 'End'],
    hideCapacity: ['Kapazität verstecken', 'Hide capacity'],
    qrCodeValidityInMinutes: ['Gültigskeitsdauer QR-Code (in Minuten)', 'Validity duration of qr code (in minutes)'],
    inheritedQrCodeValidityInMinutes: [
      'Gültigskeitsdauer QR-Code (in Minuten) (abgeleitet)',
      'Validity duration of qr code (in minutes) (inherited)',
    ],
    slots: ['Zeitslots', 'Time slots'],
    settings: ['Einstellungen', 'Settings'],
    beraterAvailabilities: ['Verfügbarkeit Berater', 'Availibility counselors'],
    terminCount: ['Anzahl Termine', 'Termin count'],
    productCount: ['Anzahl Produkte', 'Product count'],
    userID: ['Nutzer Referenz (intern)', 'User reference (intern)'],
    productID: ['Produkt Referenz (intern)', 'Product reference (intern'],
    qrcode_valid: [
      'Wie lange soll der QR Code für die Teilnahmebestätigung über das Ende der Veranstaltung hinaus gültig sein?',
      'How long should the QRcode for the participation confirmation be valid for, after the event ended?',
    ],
    topic: ['Thema', 'Topic'],
    extraParticipationConstraints: ['Bedingungen für eine erfolgreiche Teilnahme', 'Participation Constraints'],
    inheritedExtraParticipationConstraints: [
      'Bedingungen für eine erfolgreiche Teilnahme (abgeleitet)',
      'Participation Constraints (derived)',
    ],
    language: ['Sprache', 'Language'],
    attachedCertificate: ['beigefügtes Zertifikat', 'attached Certificate'],
    slotConfig: ['Buchungsslot Konfiguration', 'Bookingslot config'],
    presenceType: ['Präsenz', 'Presence'],
    anbieter: ['Anbieter', 'Provider'],
    whatsappNotification: ['WhatsApp aktiviert', 'WhatsApp enabled'],
    whatsappPhone: ['Whatsapp Telefonnummer', 'Whatsapp phonenumner'],
    ikh_doNotSendInvoice: ['Keine Rechnung versenden', 'Do not send Invoice'],
    fulfilledParticipationRequirements: ['Teilnahmeanforerungen erfüllt', 'Participation requirements fullfiled'],
    timeslotID: ['Zeitslot ID', 'Timeslot ID'],
    translatedDescriptionHTML: ['Beschreibung Übersetzung', 'Description translation'],
    text: ['Text', 'Text'],
    langKey: ['Sprach Key', 'Language key'],
    zipCity: ['Stadt', 'City'],
    bundesland: ['Bundesland', 'Bundesland'],
    additionalInfo: ['weitere Infos', 'additional Information'],
    descriptionsHTML: ['Beschreibung', 'Description'],
    exportDate: ['Export Datum', 'export date'],
    colorDefinitions: ['Farben', 'Farben'],
    beraterIDs: ['Berater IDs', 'Berater IDs'],
    inheritedInfoLink: ['Info Link (abgeleitet)', 'info link (derived)'],
    autoNachrueckenEnabled: ['auto Nachrücken aktiv', 'automatic moveup enables'],
    inheritedAutoNachrueckenEnabled: ['auto Nachrücken aktiv (abgeleitet)', 'automatic moveup enabled (derived)'],
    autoMoveNoShowEnabled: [
      'verschiebe Teilnehmer im Status "verbindlich angemeldet" automatisch in den Status "Nicht erschienen", wenn sie 7 Tage nach Ende der Veranstaltung noch nicht als teilgenommen markiert sind',
      'move participants in status "bindingly registered" automatically to status "No-Show", if they have not been marked as participated for 7 days after the event ended',
    ],
    inheritedAutoMoveNoShowEnabled: [
      'auto No-Show-Setzen aktiv (abgeleitet)',
      'automatic no-show moving enabled (derived)',
    ],
    sendNewsletterEnabled: ['Newsletter "Weitere Workshops" versenden', 'send "Further Workshops" newsletter'],
    inheritedSendNewsletterEnabled: ['Newsletter versenden (abgeleitet)', 'send newsletter (derived)'],
    autoSendCertifatesEnabled: ['auto Zertifikat Versandt aktiviert', 'send certificates automatically'],
    inheritedAutoSendCertifatesEnabled: [
      'auto Zertifikat Versandt aktiviert (abgeleitet)',
      'send certificates automatically (derived)',
    ],
    interval: ['Interval', 'interval'],
    bundleID: ['Bundle ID', 'bundle id'],
    scope: ['Bereich', 'Scope'],
    accentColor: ['accent Farbe', 'accent Color'],
    accentContrastFontColor: ['accent Kontrast Schriftfarbe', 'accent contrast font color'],
    currentPayment: ['Zahlung', 'Payment'],
    payment: ['Zahlung', 'Payment'],
    tax: ['Steuer', 'Tax'],
    status: ['Status', 'State'],
    products: ['Workshops', 'Workshops'],
    amount: ['Betrag (Cents)', 'Amount (Cents)'],
    orderNr: ['Rechunugsnummer', 'Invoice Nr.'],
    discount: ['Rabatt', 'Discount'],
    year: ['Jahr', 'Year'],
    month: ['Monat', 'Month'],
    day: ['Tag', 'Day'],
    workshopIDs: ['Workshops', 'Workshops'],
    nr: ['nr.', 'nr.'],
    priceID: ['Price ID', 'Price ID'],
    type: ['Typ', 'Type'],
    iban: ['IBAN', 'IBAN'],
    contractPrice: ['Vergütung', 'Salary'],
    signedContractFileID: ['Unterschriebener Vertrag', 'Signed Contract'],
    contractID: ['Vertrag', 'Contract'],
    lecturer: ['Dozent', 'Lecturer'],
    lecturers: ['Dozenten', 'Lecturers'],
    isNoShow: ['No-Show', 'No-Show'],
    userStammdaten: ['Nutzer Daten', 'user data'],
    suspension: ['Suspendierung', 'Suspension'],
    'suspension.message': ['Suspendierungs Grund', 'suspension reason'],
    lastChange: ['letzte Änderung', 'last change'],
    fileName: ['Dateiname', 'Filename'],
    mimetype: ['Mimetype', 'Mimetype'],
    sd_sendSemesterMail: ['Automatischer Rechnungsversand', 'Automatic invoice sending'],
    certificateTemplate: ['Zertifikat Vorlage', 'Certificate template'],
  },
  validation_error: {
    number_required: ['Bitte geben Sie eine Zahl ein', 'Please insert a number'],
  },
  enums: {
    ...demo.enums,
    ...ikh.enums,
    ...sd.enums,
    ...sdel.enums,
    ...cpg.enums,
    ...pbs.enums,
    ...fjn.enums,
    ...mzs.enums,
    ...st.enums,
    ...tuda.enums,
    ...icsrm.enums,
    ...icsrmFollowUp.enums,
    ...csjensblank.enums,
    ...digitell.enums,
    ...buildYourOwnAi.enums,
    PLANNED: ['Geplant', 'Planned'],
    RUNNING: ['Laufend', 'Running'],
    COMPLETED: ['Abgeschlossen', 'Completed'],
  },

  confirm_mail: {
    name: ['E-Mail bestätigen', 'Confirm e-mail'],
    expl: [
      'Hiermit bestätigen Sie Ihre Identität und bekommen hiermit Zugriff auf die Registrierung der Workshops.',
      'Here you are confirm your identity and get access to the workshop registration.',
    ],
    to_profil: ['Zum Profil', 'goto profile'],
    token_is_invalid: ['Token ist invalid', 'Token is invalid'],
    verify_success: ['E-Mail erfolgreich bestätigt', 'E-Mail sucessfully validated'],
    verify_success_expl: [
      'Ihre E-Mail ist nun bestätigt und Sie können Registrierungen durchführen.',
      'E-Mail successfully validated. You can now register.',
    ],
  },

  participated: {
    invalid_qrcode: ['Ungültiger QRCode', 'Invalid QRCode'],
    invalid_qrcode_no_participation: [
      'Sie müssen zu dieser Veranstaltung angemeldet sein um teilnzunehmen!',
      'You must be registered for this workshop in order to participate!',
    ],
    you_are_sus: ['Sie sind suspendiert!', 'You are suspended!'],
    successfull_participation: ['Erfolgreich am Workshop  teilgenommen.'],
    error: {
      not_valid_yet: ['Dieser Link ist noch nicht freigegeben', 'This link is not valid yet'],
      expired: ['Dieser Link ist nicht mehr gültig', 'This link is expired'],
      invalid_token: ['Dieser Link ist ungültig', 'This link is invalid'],
      only_active_participatns_allowed: [
        'Nur aktive Teilnehmer können diesen Link nutzen',
        'Only active participants can use this link',
      ],
    },
    nothing_found_for_this_account: [
      'Unter diesem Account wurde keine Anmeldung zum Workshop gefunden.',
      'No registration found for this account.',
    ],
    do_you_want_to_login_with_different_account: [
      'Bitte loggen Sie sich mit dem Account ein, mit dem Sie sich zur Veranstaltung angemeldet haben.',
      'Please log in with the account you used to register for this workshop.',
    ],
    login_with_different_account: ['Login mit einem anderen Account', 'Login with a different account'],
  },

  err: {
    unexpected: ['Unerwarteter Fehler', 'Unexpected error'],
    contact_admin: ['Bitte kontaktieren Sie Ihren Administrator', 'Please contact your administrator'],
    something_went_wrong: ['Da ist etwas schiefgelaufen', 'Something went wrong'],
    could_not_process_data: ['Die Daten konnten nicht verarbeitet werden', 'The data processing failed'],
    no_evaluation_link: [
      'Für diesen Workshop ist kein Evaluationslink konfiguriert',
      'No evaluation link configured for this workshop',
    ],
    cert_gen_failed_missing_cert: [
      'Fehler: Für diesen Workshop wurde kein Zertifikat erstellt',
      'Cannot generate certificate for workshop without linked certificate',
    ],
    cert_mails_not_enabled: [
      'Die Einrichtung hat den Teilnahmezertifikat-Versand nicht aktiviert.',
      'The institution has not enabled the sending of participation certificates.',
    ],
    missing_permission: [
      'Ihnen fehlen die erforderlichen Berechtigung um diese Aktion durchzuführen',
      "You don't have the necessary permissions to perform this action",
    ],
  },

  PARTICIPATION_DATAVIEW: {
    registerTo: ['Anmeldung zu', 'Register to'],
    addParticipant: ['Teilnehmer hinzufügen', 'add participant'],
    modifyParticipationStatus: ['Teilnahmestatus anpassen', 'modify participation status'],
    confirmModifyParticipationStatus: [
      'Wollen Sie den Teilnahmestatus von <user> wirklich auf "<status>" setzen?',
      'Do you really want to change the participation status of <user> to "<status>"',
    ],
    participated: ['Hat teilgenommen', 'Did participate'],
    didNotParticipate: ['Hat nicht teilgenommen', 'Did not participate'],
    didParticipate: ['Hat teilgenommen', 'Did participate'],
  },

  OTP: {
    title: ['Mit der E-Mail-Adresse anmelden', 'Log in with your E-Mail address'],
    subtitle: [
      'Wir haben Ihnen einen 6-stelligen Code geschickt. Geben Sie diesen hier ein, um sich einzuloggen.',
      'We sent you a 6-digit code. Please enter this code to log in.',
    ],
    check: ['Überprüfen', 'Check'],
    resendCode: ['Code erneut zusenden', 'Send code again'],
    problemOccuredSendingOTP: [
      'Es gab ein Problem beim Versenden des Codes. Bitte versuche es später erneut.',
      'There was a problem sending your code. Please try again!',
    ],
    mail_login_user_non_existent_message: [
      'Es gibt kein Benutzerkonto mit dieser E-Mail Adresse. Es konnte kein Code gesendet werden.',
      'There is no account for this mail address. No code could be send.',
    ],
  },

  // errors
  BLOCK_SIGNOFF_ERROR: ['Abmeldung ist nicht mehr möglich', 'Sign out is not possible anymore'],
  BLOCK_SIGNIN_ERROR: [
    'Die Anmeldefrist für diese Veranstaltung ist bereits abgelaufen.',
    'The registration deadline for this event has already passed.',
  ],
  REGISTRATION_NOT_YET_OPEN_ERROR: [
    'Die Anmeldung für diese Veranstaltung ist noch nicht geöffnet.',
    'Registration for this event is not open yet.',
  ],
  REGISTRATION_EMAIL_NOT_CONFIRMED_ERROR: [
    'Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse. Wir haben Ihnen einen Code geschickt.',
    'Please confirm your e-mail address first. We have sent you a code.',
  ],
  errors: {
    RegistrationWrongCodeError: {
      explanation: ['Falscher Code eingegeben. Versuchen Sie es erneut.', 'Wrong Code. Please try again.'],
    },
    RegistrationTooManyAttemptsError: {
      explanation: ['Zu oft den falschen Code eingegeben!', 'Too many wrong attempts!'],
    },
    not_authorized: ['Fehlende Berechtigung', 'Not authorized'],
    NO_PARTICIPANTS_AVAILABLE_ERROR: [
      'Es wurden keine Nutzer gefunden für die Zertifikate generiert werden könnten',
      'There are no users for which certificates could be generated',
    ],
  },

  dropdowns: {
    emptyStateUsers: ['Keine Personen gefunden', 'No users found'],
    emptyStateUsersWithoutSearch: [
      'Anfangen zu Tippen um nach Nutzern zu suchen...',
      'Start typing to search for users...',
    ],
    emptyStateTimeframes: ['Keine Semester gefunden', 'No semesters found'],
    emptyStateCertificates: ['Keine Zertifikate gefunden', 'No certificates found'],
    emptyStateWorkshops: ['Keine Workshops gefunden', 'No workshops found'],
  },

  languages: {
    de: ['Deutsch', 'german'],
    en: ['Englisch', 'english'],
  },

  ikh,
  sd,
  sdel,
  cpg,
  pbs,
  fjn,
  mzs,
  st,
  digitell,
  tuda,
  icsrm,
  icsrmFollowUp,
  deutschkurs,
  csjensblank,
  schluesselkompetenzen,
  demo,

  mail_template: {
    changedRegistration: ['Diese Anmeldung hat sich verändert', 'This registration has changed'],
    participated: ['Teilgenommen', 'Participated'],
    status: ['Status', 'Status'],
    topic: ['Thema', 'Topic'],
    language: ['Sprache', 'Language'],
    waitinglistPlace: ['Wartelistenplatz', 'Waitinglist place'],
    start: ['Start', 'Start'],
    end: ['Ende', 'End'],
    location: ['Ort', 'Location'],
    appointment: ['Termin', 'Appointment'],
    new: ['Neu', 'New'],
    removed: ['Entfernt', 'Removed'],
    use_template: ['Vorlage verwenden', 'Use template'],
    date: ['Datum', 'Date'],
  },
  share: {
    whatsapp: ['WhatsApp', 'WhatsApp'],
    facebook: ['Facebook', 'Facebook'],
    twitter: ['X', 'X'],
    linkedin: ['LinkedIn', 'LinkedIn'],
    share: ['Teilen', 'Share'],
    content: ['Ich nehme an dieser Veranstaltung teil:', 'I take part in this event:'],
  },

  types: {
    enum: ['Auswahlliste', 'Selection List'],
    string: ['Textfeld', 'Text Field'],
    boolean: ['Checkbox', 'Checkbox'],
    int: ['Zahlfeld', 'Number Field'],
  },
  enum_values: ['Mögliche Werte:', 'Possible Values:'],
} as const;

export type BHTranslationDictionary = Record<string, any>;
